import type { Translations } from "../index";

export const zhTW: Translations = {
  lang: {
    en: "EN",
    ja: "日本語",
    "zh-HK": "繁中",
    "zh-TW": "繁中",
    "zh-CN": "简中",
    ko: "한국어",
    th: "ไทย",
    ru: "RU",
    current: "繁體中文（台灣）",
  },

  nav: {
    platform: "平台",
    forDesigners: "設計師專區",
    forBrands: "品牌專區",
    forManufacturers: "生產",
    forStudio: "Studio",
    caraAi: "Cara AI",
    pricing: "定價",
    more: "更多",
    about: "關於我們",
    academy: "學院",
    accessPlatform: "進入平台 →",
    subline: "設計邂逅生產之地",
    sublineBrand: "採購 · 投產 · 上市",
    sublineDesigner: "珠寶設計師的舞台",
    inspire: "靈感",
    newsroom: "新聞中心",
    contact: "聯絡我們",
    catalog: "產品目錄",
    showcase: "設計展示",
    switchToDesigners: "設計師專區 →",
    switchToBrands: "品牌專區 →",
    howItWorks: "運作方式",
    overview: "概覽",
  },

  footer: {
    subline: "設計師珠寶 · B2B 採購 · EPH 製造",
    tagline: "「卓越設計與無瑕製造\n在此交匯。」",
    accessPlatform: "進入平台 →",
    ephCertified: "EPH 認證製造",
    copyright: "Caratzon Ltd. 版權所有。",
    columns: {
      platform: "平台",
      company: "公司",
      resources: "資源",
      contact: "聯絡",
    },
    links: {
      forDesigners: "設計師專區",
      forBrands: "品牌專區",
      forManufacturers: "生產",
      caraAi: "Cara AI",
      tradeCatalogue: "貿易目錄",
      accessPlatform: "進入平台",
      aboutCaratzon: "關於 Caratzon",
      ourStory: "我們的故事",
      leadership: "管理團隊",
      pressMedia: "媒體報導",
      investorRelations: "投資者關係",
      howItWorks: "平台運作方式",
      ephHub: "EPH 製造基地",
      sustainabilityReport: "可持續發展報告",
      cadStandards: "CAD 規格標準",
      supplierCode: "供應商行為守則",
      academy: "學院",
      inspire: "靈感",
      newsroom: "新聞中心",
      aiCredits: "AI 積分",
      contactUs: "聯絡我們",
      becomeManufacturer: "成為製造合作夥伴",
    },
    offices: {
      hongkong: "設計門戶 · 香港",
      guangdong: "製造基地 · 廣東",
      london: "歐洲貿易辦事處",
    },
    legal: {
      terms: "服務條款",
      privacy: "私隱政策",
      cookies: "Cookie 政策",
      supplierCode: "供應商守則",
    },
  },

  home: {
    meta: {
      title: "Caratzon — B2B 珠寶採購 · 預工程 CAD · AI 行銷",
      description:
        "頂級 B2B 獨立精品珠寶採購平台。授權預工程 CAD 設計，在 EPH 進行可持續生產，透過 Caratzon Studio 即時生成行銷素材。三級 IP 授權，端到端生產管理。",
    },
    emergence: {
      tagline: "從草稿到貨架。設計、授權、生產——只需 Caratzon。",
      subtitle: "珠寶設計、授權與生產的 B2B 平台",
      cta: "瀏覽目錄",
      ctaSecondary: "了解運作方式",
    },
    process: {
      overline: "運作方式",
      steps: [
        { number: "01", heading: "發現", body: "瀏覽來自全球獨立設計師的預製原創作品。", hook: "無需研發成本。無需開模費用。" },
        { number: "02", heading: "授權", body: "選擇您的知識產權層級，數秒內取得商業授權。", hook: "每份授權均附帶營銷素材。" },
        { number: "03", heading: "生產", body: "設計直接送入 EPH 的可持續製造中心。完成。", hook: "零工廠管理。全程可追溯。" },
      ],
    },
    storyBeat: {
      line1: "從草圖到貨架。",
      line2: "設計、授權、製造。",
      line3: "唯有 Caratzon。",
      splitLabel: "我是",
      splitDesigner: "設計師",
      splitBrand: "品牌買家",
    },
    showcase: {
      pieces: [
        {
          sku: "FD001-251225032",
          designer: "雅玉工作室",
          name: "天幕垂鏈",
          descriptor: "流金懸光，靜待綻放。",
        },
        {
          sku: "FD016-251224017",
          designer: "金葫蘆",
          name: "福祿葫蘆吊墜",
          descriptor: "古韻符號，當代輪廓。",
        },
        {
          sku: "FD033-251225049",
          designer: "牡丹工坊",
          name: "花瓣垂耳環",
          descriptor: "花瓣半落，凝於瞬間。",
        },
        {
          sku: "FD051-251226013",
          designer: "玉河設計",
          name: "漣漪手鐲",
          descriptor: "水波凝腕，流金定格。",
        },
      ],
    },
    stats: [
      { value: "48+", numericValue: 48, label: "原創設計" },
      { value: "12", numericValue: 12, label: "獨立設計師" },
      { value: "6", numericValue: 6, label: "國家" },
      { value: "80%", numericValue: 80, label: "設計師收入分成", suffix: "%" },
    ],
    fork: {
      heading: "現在，你屬於哪個世界？",
      designer: {
        eyebrow: "設計師專區",
        line: "你的工藝值得一個世界舞台——而不是又一則 Instagram 貼文。",
        cta: "以設計師身份進入",
      },
      brand: {
        eyebrow: "品牌專區",
        line: "跳過研發。授權附帶營銷素材。今天就上架。",
        cta: "以品牌身份進入",
      },
    },
    close: {
      line: "下一個篇章，由此開始。",
      cta: "立即開始",
    },
    hero: {
      overline: "B2B 採購平台 · 經 EPH 可持續生產",
      h1Line1: "採購設計。",
      h1Line2: "跳過研發。",
      h1Line3: "即刻上市。",
      tagline: "頂級 B2B 獨立精品珠寶採購平台。授權預工程 CAD 設計，在 EPH 樞紐進行可持續生產，AI Studio 在您點擊「購買」的瞬間自動生成行銷素材。",
      cta: "瀏覽貿易目錄",
      ctaSecondary: "了解 Caratzon Studio",
    },
    pillars: {
      overline: "平台能力",
      subtitle: "端對端工作流程",
      items: [
        {
          heading: "AI 行銷引擎（Caratzon Studio）",
          body: "無需等待數週拍攝樣品照片。每份授權均包含 Essentials Kit（電商產品圖、規格及 SEO 文案）。升級至 Caratzon Kit 可自動生成生活風格攝影、社交媒體素材及以您品牌色彩定制的 PDF 批發單。",
        },
        {
          heading: "三級 IP 授權，法律文件保障",
          body: "標準（非獨家）、季節性禁售（12 個月獨家）或完整 Vault IP（永久 IP 轉讓）。具法律約束力的商業權利——不僅僅是握手協議。",
        },
        {
          heading: "端到端 EPH 生產",
          body: "您的訂單直接路由至我們經驗證的 EPH 製造網絡。鑄造、鑲嵌、品質管控及物流均在一個統合的可持續樞紐中管理。無需工廠談判。",
        },
      ],
    },
    quote: {
      text: "「下一季系列——無研發、無代理、無工廠需管。\n採購到上架，一平台搞定。」",
      attribution: "Caratzon 理念",
    },
    marquee: {
      ariaLabel: "目錄預覽",
    },
    howItWorks: {
      overline: "運作方式",
      subtitle: "三個步驟",
      items: [
        {
          heading: "發現",
          body: "瀏覽獨立創作者的預工程原創設計。預覽每件作品的 Essentials Kit——型錄照片、規格、編輯故事及互動 3D 瀏覽器——在承諾前評估完整套裝。",
        },
        {
          heading: "授權與生成",
          body: "選擇您的 IP 層級（標準、禁售或 Vault），數秒內取得商業權利。授權完成的瞬間，Caratzon Studio 即為您生成完整行銷套件，讓您的銷售團隊立即開始工作。",
        },
        {
          heading: "EPH 生產",
          body: "已批准的設計路由至 EPH 統合設施。可持續電鍍、認證環保流程、端到端里程碑追蹤——而您的行銷素材已經上線。",
        },
      ],
    },
    eph: {
      overline: "製造夥伴",
      location: "中國",
      headline: "中國最大可持續一站式珠寶及環保電鍍製造基地",
      body: "所有經 Caratzon 下達的生產訂單均於 EPH 一站式廠房完成——鑄造、鑲嵌、拋光及環保電鍍同一屋簷下進行，全面符合環保法規，生產進度逐步可追溯。",
      attrs: [
        {
          label: "環保電鍍",
          body: "低排放環保電鍍產線——黃金、銠及合金表面處理，經驗證減少化學廢物，採用閉環水循環系統。",
        },
        {
          label: "一站式生產設施",
          body: "鑄造、鑲嵌、拋光及電鍍同一廠房完成。減少交接、提升精度、縮短生產週期。",
        },
        {
          label: "系統直接對接",
          body: "Caratzon 平台上獲批的設計自動進入 EPH 生產排程，毋須人手重新下單，供應鏈零斷層。",
        },
        {
          label: "全程可追溯",
          body: "每個生產階段均記錄於您的 Caratzon 控制台——由原材料收貨到最終品管簽收，一目了然。",
        },
      ],
    },
    forWhom: {
      overline: "適用對象",
      heading: "專為\n貿易買家而設。",
      items: [
        {
          title: "珠寶零售商",
          desc: "採購競爭對手無法找到的設計師直送作品。每份授權均包含 Essentials Kit——型錄照片、規格及 SEO 文案即時就緒。升級至 Caratzon Kit 獲取生活風格攝影及社交素材。授權當天即可上架網站、加入型錄並發佈 Instagram。",
        },
        {
          title: "精品店主",
          desc: "跳過每件設計 $1,500 以上的 NRE（非經常性工程）成本。授權經測試的生產就緒原創設計。生成您的 Caratzon Kit，在簽署授權的同一天將新品上架 Shopify。",
        },
        {
          title: "多品牌經銷商",
          desc: "透過單一供應商檔案從數十位全球獨立設計師採購。自動生成格式完美的 PDF 批發單，即時分享給您的零售客戶。",
        },
      ],
    },
    caraAI: {
      overline: "Cara AI · 工程智能",
      heading: "為工廠\n而設計的 AI。",
      body: "Cara 不會生成幻想渲染圖。它工程化設計能夠通過失蠟鑄造、寶石鑲嵌，並在手上佩戴一整天仍感舒適的珠寶——然後在您喝完咖啡之前生成完整的物料清單。",
      stats: [
        { value: "±0.15mm", label: "每種合金的收縮補償" },
        { value: "0.6mm", label: "強制最小壁厚" },
        { value: "4-Prong+", label: "寶石固定工程設計" },
        { value: "R≥0.4mm", label: "全天舒適的邊緣半徑" },
      ],
      cta: "試用 Cara AI — 即場示範",
      ctaSecondary: "了解更多",
    },
    inspirePreview: {
      overline: "在 Inspire 上探索",
      heading: "瀏覽。收藏。採購。",
      body: "歡迎來到 Caratzon Inspire — 您的 B2B 靈感動態。瀏覽全球頂尖 3D CAD 設計的精選串流。建立您品牌的靈感板，關注您喜愛的獨立創作者，一鍵從靈感轉化為生產。",
      cta: "探索 Inspire",
    },
    designerBanner: {
      overline: "設計師專區",
      heading: "您的 CAD 檔案值得\n更好的作品集。",
      body: "加入我們精選的全球 3D 創作者網絡。上傳您的預工程設計，自動解鎖專屬的 Caratzon Inspire 作品集。讓我們的 AI 免費渲染您的檔案，向數千名認證零售買家展示您的作品，並賺取高利潤版稅。",
      cta: "申請您的設計師作品集",
    },
    footerCta: {
      overline: "準備好採購了嗎",
      heading: "下一季系列。\n一平台。零斷層。",
      cta: "瀏覽貿易目錄",
      tradeOnly: "僅限貿易帳戶",
    },
  },

  forDesigners: {
    meta: {
      title: "設計師專區 · Caratzon",
      description:
        "您的故事，由 AI 精心呈現。您的設計，面向全球買家。加入 Caratzon Inspire——上傳 CAD、回答幾條關於創作歷程的問題，AI 即為您撰寫設計師故事。每筆授權費賺取 80%，並享有持續版稅。",
    },
    hero: {
      overline: "專為獨立珠寶設計師而設",
      h1Line1: "您的故事。",
      h1Line2: "精彩呈現。",
      tagline:
        "回答幾條關於您的工藝、靈感和願景的問題。我們的 AI 將您的回答化為引人入勝的設計師故事——並將您的作品呈現在全球買家面前。",
      cta: "成為靈感之源 →",
    },
    stats: {
      share: "您的授權費分成",
      ipProtected: "IP 受保護",
      cadNote: "CAD 絕不向買家披露",
      perpetual: "持續版稅",
      royaltiesNote: "每件生產單品均可收取版稅",
      zeroMarketing: "零推廣負擔",
      portfolioNote: "AI 為您建立故事",
    },
    pain: {
      overline: "現實",
      p1: "您是一位才華洋溢的珠寶設計師。您的作品原創、精緻，隨時可以投產。",
      p2: "但為了維持生計，您被迫身兼社交媒體管理、文案撰寫、生活風格攝影及工廠聯絡等多重角色。您最出色的作品擱在硬碟裡，因為您既沒有時間、也沒有資金將它們推廣給全球 B2B 品牌。",
      quote: "我們創立 Caratzon，讓全球最有才華的珠寶設計師可以專注於他們最擅長的事——設計。",
      quoteAttribution: "— Caratzon 創辦團隊",
    },
    inspire: {
      overline: "成為靈感之源",
      subtitle: "您的故事，由 AI 精心雕琢。",
      heading: "七條問題。\n一個難忘的故事。",
      p1: "入駐過程中，我們會問您的創作起源、設計理念、靈感來源，以及作品背後的情感世界。這不是填表格——而是一場關於您作為創作者的對話。",
      p2: "我們的 AI 將您的回答轉化為雜誌級設計師檔案——標題、敘事、主題章節及品牌關鍵詞。這種故事能讓買家停下來，主動聯繫您。",
      features: [
        { label: "您的起源，您的聲音", description: "我們會問您如何踏上設計之路、哪些材料令您著迷、哪些文化影響塑造了您的美學。您的話語就是公開故事的根基。" },
        { label: "理念與內心世界", description: "佩戴您的珠寶時應有甚麼感覺？甚麼創作張力定義了您的作品？這些深層問題賦予 AI 心理質感，寫出像您本人而非範本的內容。" },
        { label: "發佈於 Inspire", description: "您的 AI 生成故事將於 Inspire 創作者頁面上線——買家、傳媒及合作夥伴均可發現的公開檔案。隨時在 Studio 控制台編輯。" },
      ],
      previewLabel: "AI 生成預覽",
      previewTitle: "張力 — Aria 的世界",
      previewBody: "她設計珠寶的方式如同建築師思考光線——不是為了裝飾，而是為了揭示。",
      previewExtended: "在俯瞰博斯普魯斯海峽的陽光工作室裡，Aria Atelier 在精準與狂野的交匯處創作。她的作品始於質感紙上的水彩草圖——她需要先感受重量，才會觸碰螢幕⋯⋯",
      previewKeywords: ["張力", "黃金", "建築感", "侘寂", "低調自信", "博斯普魯斯之光"],
      cta: "成為靈感來源 →",
    },
    designOfWeek: {
      overline: "每週設計精選",
      subtitle: "全球推薦。每週甄選。",
      heading: "獲選\n每週設計精選。",
      badgeLabel: "本週設計",
      featuredTitle: "稜鏡花瓣戒指",
      featuredAttribution: "by Aria Atelier · 2026年3月3日精選",
      p1: "每週，Caratzon 編輯團隊會挑選一件傑出設計在全平台重點展示。獲選設計師將獲得 Inspire 首頁的顯眼位置、專屬編輯特寫，以及面向整個買家網絡的直接曝光。",
      p2: "每週設計精選不僅是曝光——更是向買家發出的信號：您的作品已獲業內最嚴格策展人的認可。過往獲選者已促成授權交易、傳媒報導及長期品牌合作。",
      benefits: [
        { label: "首頁焦點", description: "您的設計和故事將在 Inspire 首頁顯眼展示七天——每位買家到訪平台時首先看到的作品。" },
        { label: "編輯特寫", description: "以您的 AI 生成故事為基礎撰寫的專屬編輯文章，補充設計靈感、材料及製造方式的背景。" },
        { label: "買家網絡通知", description: "平台上每位活躍買家均會收到精選通知，展示您的設計。直接通往授權洽商的渠道。" },
      ],
    },
    platform: {
      overline: "平台如何為您服務",
      subtitle: "零付出。最大觸及。",
      items: [
        {
          heading: "AI 為您講故事",
          body: "入駐時回答幾條關於您工藝的問題。我們的 AI 撰寫您的設計師故事、生成品牌關鍵詞，並發佈您的 Inspire 創作者頁面——一份隨您的作品持續更新的活檔案。",
        },
        {
          heading: "直達全球買家",
          body: "您的自動化作品集將呈現在正在積極採購的大型零售品牌採購經理及買手面前。您毋須主動推銷——他們瀏覽我們的精選展示，自然發現您。",
        },
        {
          heading: "零生產煩惱",
          body: "品牌購入您的設計後，您毋須與任何工廠溝通。我們的平台安全地將檔案分配至嚴選製造夥伴。鑄造、品管及物流全由我們處理。",
        },
        {
          heading: "滴水不漏的 IP 保護",
          body: "無論哪種授權模式，您的 CAD 檔案絕不向買家披露。品牌僅獲得生產權。您的源檔案僅在 NDA 下提供給我們嚴選的製造夥伴。只有全面 IP 收購才會轉讓設計檔案。",
        },
      ],
    },
    collections: {
      overline: "作品系列 + AI 故事",
      subtitle: "您的設計值得不止於一個列表。",
      heading: "組合您的作品。\nAI 講述故事。",
      p1: "選擇兩件或以上已發佈的設計，我們的 AI 會找出連繫它們的脈絡——共同材質、氛圍、創意感覺——並圍繞這條主線構建完整展示。",
      p2: "品牌看到的不只是零散單品，而是一位擁有連貫創意視野和完整作品體系的設計師。這才是令他們主動聯繫您的關鍵。",
      features: [
        {
          label: "每件作品統一聲調",
          description: "AI 辨識貫穿您設計的創意主線——共同色調、氛圍、靈感——讓買家看到連貫的創意視野，而非雜亂的檔案列表。",
        },
        {
          label: "您的作品講述同一個故事",
          description: "一篇連貫的敘事，逐一點名每件設計並建立關聯。買家在查詢前就能理解您系列背後的理念。",
        },
        {
          label: "每個系列都達雜誌水準",
          description: "雜誌級展示採用統一編輯模板——封面視覺、作品特寫，以及所有系列一致的視覺風格。",
        },
      ],
      cta: "在 Studio 探索作品系列 →",
    },
    revenue: {
      overline: "您的收入，三種方式",
      subtitle: "三種獲利方式",
      heading: "品牌有三種購買方式。\n您有三種收入途徑。",
      footnote:
        "三種授權模式下，您均保留授權費的 80%。Caratzon 保留 20% 用於平台營運、推廣及製造協調。標準授權和季節性獨家授權為您帶來持續版稅——每件生產單品均可收取，初始交易完成後您的設計仍持續產生收入。完整獨家版權是一次過、乾淨俐落的 IP 買斷：無持續版稅，僅一筆由您自訂底價的高額付款。",
      models: [
        {
          tier: "標準",
          label: "非獨家生產授權",
          heading: "標準授權",
          description:
            "品牌獲得透過 Caratzon 製造網絡生產您設計的權利。非獨家——多個品牌可同時持有標準授權。您從每筆授權費中賺取 80%，並從每件生產單品中持續收取版稅。",
          benefit: "80% 歸您 · 持續版稅 · 設計留在市場",
        },
        {
          tier: "高級",
          label: "12 個月獨家製造權",
          heading: "季節性獨家授權",
          description:
            "品牌支付溢價，在 12 個月內獨佔您的設計。推廣窗口期內，您的作品從市集隱藏——競爭對手無法生產。您賺取獨家費用的 80% 及每件生產單品的持續版稅。窗口期結束後，設計重返公開市場。",
          benefit: "80% 歸您 · 更高前期收入 · 有時限 · 可重複",
        },
        {
          tier: "金庫",
          label: "完整獨家版權",
          heading: "完整獨家版權",
          description:
            "品牌獲得永久擁有權，直接取得原始生產檔案（.STL / .3DM）。設計從市集永久下架。您獲得一筆由您自訂底價的高額付款的 80%。乾淨退出：一次付款，設計退場，無後續義務。",
          benefit: "80% 歸您 · 您訂底價 · 一次過乾淨退出",
        },
      ],
    },
    steps: {
      overline: "由入駐到收入",
      subtitle: "三個步驟",
      items: [
        {
          heading: "成為靈感之源",
          body: "回答關於您的工藝、理念和靈感的問題。我們的 AI 生成您的設計師故事並發佈到 Inspire 頁面——買家發現您的公開創作者檔案。上傳您的首個 CAD 檔案以完善作品集。",
        },
        {
          heading: "AI 建立您的品牌",
          body: "我們的 Story Lab 為每件作品生成影樓級 lookbook 攝影及編輯敘事。將設計組合成系列，AI 為所有作品撰寫統一故事。獲選每週設計精選可獲最大曝光。",
        },
        {
          heading: "收取收入",
          body: "品牌發現您的作品並提出授權請求。您批准或反建議條款。確認後，生產自動啟動——鑄造第一克金屬之前，80% 授權費已到賬，版稅隨每件生產單品持續流入。",
        },
      ],
    },
    bottomLine: {
      overline: "結語",
      p1: "您的工作是創作。我們的 AI 講述您的故事。我們的平台銷售您的作品。",
      p2: "由第一條入駐問題到第一筆授權交易——我們處理敘事、攝影、買家發掘及製造。您只管設計。",
      shareLabel: "歸您所有",
      shareNote:
        "每筆授權費的 80% 歸您。標準授權和季節性獨家授權每件生產單品均有持續版稅。完整獨家版權收購是一次過溢價，底價由您自訂。Caratzon 的 20% 用於支持推廣引擎——在您入睡後，它仍在為您的作品銷售。",
    },
    cta: {
      overline: "成為靈感之源",
      heading: "您的故事。\n您的舞台。",
      primary: "成為靈感之源 →",
      secondary: "已是設計師？開啟 Studio →",
    },
    badges: {
      premium: "高級",
    },
  },

  forBrands: {
    meta: {
      title: "品牌商專區 · Caratzon",
      description:
        "向獨立設計師採購原創珠寶設計——每份授權連同 lookbook 攝影、產品圖及編輯故事一併交付。標準授權、獨家授權或完整版權收購。生產端對端代管。",
    },
    howItWorks: {
      meta: {
        title: "運作方式 — 品牌版 Caratzon",
        description: "從發現到交付——瀏覽設計師原創作品，選擇授權模式，製造網絡管理生產，行銷素材即時上線。",
      },
      headline: "從發現\n到交付。",
      intro: "瀏覽認證設計師原創作品。選擇授權方式。製造網絡管理生產，行銷素材即時上線。以下是詳細流程。",
    },
    hero: {
      overline: "珠寶品牌 · 精品店 · 分銷商",
      h1Line1: "下一季系列。",
      h1Line2: "無研發。無代理。",
      h1Line3: "無工廠需管。",
      tagline:
        "瀏覽獨立創作者原創設計。授權所愛——型錄圖、規格及行銷素材隨每件作品交付。生產經 EPH 網絡路由。採購到上架，一平台搞定。",
      ctaPrimary: "瀏覽貿易目錄",
      ctaSecondary: "查看運作方式 →",
    },
    stats: {
      tiers: "採購模式",
      designerDirect: "設計師直供",
      noIntermediaries: "零中間人",
      contentReady: "內容齊備",
      contentReadySub: "Lookbook、產品圖及故事已包含",
      productionManaged: "生產代管",
      endToEnd: "端對端",
    },
    marquee: {
      ariaLabel: "目錄預覽",
    },
    badges: {
      fullIp: "完整 IP",
    },
    acquire: {
      overline: "採購模式",
      subtitle: "您的系列，您的條款",
      heading: "三種方式採購\n一件 Caratzon 設計。",
      models: [
        {
          tier: "標準",
          label: "啟動及資產費",
          heading: "標準授權",
          description:
            "這不僅是一張許可——而是物超所值的捷徑。您買到的是一件已完成工程驗證、壓力測試並保證完美鑄造的設計，跳過所有昂貴的試錯步驟。非獨家；其他品牌可同時持有授權。",
          gets: [
            "即時投產通道——設計即時解鎖進入我們的生產線",
            "零研發成本——預先工程化的 CAD，已驗證可完美量產",
            "內容包已包含——lookbook 攝影、產品圖及編輯故事，當天即可用於店鋪",
            "商業使用權——以您自己的品牌白標銷售，永久有效",
          ],
          benefit: "免研發 · 內容齊備 · 即日上架",
        },
        {
          tier: "獨家",
          label: "12 個月獨家製造權",
          heading: "季節性獨家授權",
          description:
            "為您的下一輪推廣鎖定設計。獨家製造權為期 12 個月——設計從市集隱藏，直至獨家期屆滿。窗口期內競爭對手無法生產。期滿後設計重返市場。",
          gets: [
            "獨家鎖定——窗口期內競爭對手零生產",
            "包含所有標準授權權益",
            "完整內容包已包含——獨家 lookbook 攝影及編輯故事，12 個月內專屬於您",
            "設計在整個獨家期內從市集隱藏",
          ],
          benefit: "競爭對手隔離期 · 獨家內容 · 主打產品潛力",
        },
        {
          tier: "金庫",
          label: "完整獨家版權",
          heading: "完整獨家版權",
          description:
            "永久擁有 IP。取得永久所有權——設計即時從市集下架。付款後直接獲得可量產的原始檔案（.STL / .3DM）。任何其他品牌永遠無法採購此設計。這是唯一涉及源檔案轉讓的情況。",
          gets: [
            "永久 IP 所有權——設計從市集永久退場",
            "原始生產檔案（.STL / .3DM）直接轉讓給您",
            "完整內容包 + Lookbook Studio——以 AI 生成無限自訂攝影，配合您的品牌風格",
            "底價由設計師設定——在此基礎上洽商",
          ],
          benefit: "永久獨家 · 完整內容套件 · CAD 在手",
        },
      ],
      whyStandard: {
        label: "為甚麼要付標準授權費？",
        heading: "您買的不是一張許可證。您買的是一條價值遠超費用的捷徑。",
        points: [
          {
            heading: "省卻 NRE 開支",
            body: "傳統製造中，一件設計需花費 $500–$2,000+ 於 3D 設計費、蠟模打印、試鑄及修改，才能投入量產。標準授權取代了以上所有——檔案已完成工程化及壓力測試。",
          },
          {
            heading: "簽約當日即可上架",
            body: "授權包含影樓級 lookbook 攝影、產品圖及編輯故事——燈光、構圖、風格均達編輯標準。免攝影師、免修圖師。簽約一刻即可在您的店鋪上架。",
          },
          {
            heading: "白標銷售，零法律風險",
            body: "您獲得以自己品牌名稱及包裝銷售的完整商業權利。設計師同意不追究版權。以他們的藝術建立您的業務，零風險。",
          },
        ],
      },
      ipNotes: {
        standardLabel: "標準授權及季節性獨家授權",
        standardBody:
          "生產檔案由 Caratzon 保管，僅在 NDA 下發放給我們嚴選的製造夥伴。您不會收到原始 CAD——生產經我們的網絡自動分配。",
        vaultLabel: "完整獨家版權",
        vaultBody:
          "付款後，設計師的可量產 .STL / .3DM 檔案直接轉讓給您。設計從目錄永久退場——任何其他品牌永遠無法採購。",
      },
    },
    process: {
      overline: "運作方式",
      subtitle: "由發掘到交付",
      items: [
        {
          heading: "發掘",
          body: "瀏覽經認證的設計師原創作品。每件設計連同 lookbook 攝影、編輯故事及互動式 3D 預覽——落單前可充分評估完整內容。",
        },
        {
          heading: "授權",
          body: "選擇您的模式。提交採購請求，直接與設計師洽商條款，鎖定授權。全程有文件記錄，以法律形式規範。",
        },
        {
          heading: "投產",
          body: "訂單分配至我們的製造網絡——鑄造、鑲嵌、品管及物流端對端代管。生產進行的同時，您的 lookbook 攝影及故事已可上線。庫存未到，推廣先行。",
        },
      ],
      quote: "「採購原創。規模化生產。\n毋須自建工廠關係。」",
      bullets: [
        "橫跨亞洲及歐洲的嚴選製造商網絡",
        "鑄造、鑲嵌、品管及物流端對端代管",
        "每件設計連同 lookbook 攝影、產品圖及編輯故事一併交付",
        "Lookbook Studio：按需生成自訂攝影，配合您的品牌風格",
      ],
      teaserItems: [
        { heading: "發現", body: "瀏覽認證設計師原創作品，支援完整預覽。" },
        { heading: "授權", body: "選擇模式，鎖定商業權益。" },
        { heading: "投產", body: "素材上線的同時，製造端到端管理。" },
      ],
      teaserCta: "查看運作方式 →",
    },
    vs: {
      overline: "對比傳統批發",
      traditional: "傳統模式",
      caratzon: "Caratzon",
      rows: [
        ["來源", "來源不明，多層供應鏈", "直接來自經驗證的獨立設計師"],
        [
          "研發 / NRE 成本",
          "每件設計 $500–$2,000+（3D 設計費、蠟模打印、試鑄、修改）",
          "零——預先工程化，已驗證可完美量產",
        ],
        [
          "上市時間",
          "數週工程設計及打樣後才能上架",
          "今日授權，今日上架——內容已包含",
        ],
        [
          "推廣素材",
          "自行聘請攝影師、造型師及文案——每個系列耗費數週及數千元",
          "Lookbook 攝影、產品圖及編輯故事已包含。數小時而非數週即可推出市場",
        ],
        [
          "商業使用權",
          "需另行洽談，往往不清晰",
          "白標使用權已包含——永久以您自己的品牌銷售",
        ],
        [
          "獨家保護",
          "無機制阻止競爭對手",
          "標準授權、季節性獨家（12 個月）或完整獨家版權（永久）",
        ],
        [
          "生產",
          "自行管理工廠關係",
          "經我們的製造網絡自動分配",
        ],
      ],
    },
    studio: {
      overline: "Caratzon Studio",
      heading: "採購設計。\n我們生成行銷素材。",
      intro:
        "採購新庫存過去意味著等待數週才能拍攝樣品照片。使用 Caratzon Studio，您的行銷素材在取得授權的瞬間即自動生成。選擇您的上市策略：",
      essentials: {
        heading: "Essentials Kit",
        badge: "每份授權均包含",
        body: "無需手動輸入數據。授權設計的瞬間，即自動生成生產規格、清晰的型錄照片和 SEO 文案。今天就將新庫存上架到您的商店。",
        features: [
          "高解析度白底電商產品圖",
          "SEO 優化產品描述",
          "精確的技術與材質規格",
        ],
      },
      caratzonKit: {
        heading: "Caratzon Kit",
        badge: "高級上市升級",
        body: "無需拍攝。一鍵取代整個創意代理商。您的品牌標誌、色彩和美學無縫融入生活風格照片、批發單和社交媒體素材。",
        features: [
          "AI 生成生活風格及模特兒攝影",
          "自動排版的品牌風格 PDF 批發單",
          "即時發佈的社交媒體素材（Reels/TikTok 裁切及文案）",
        ],
      },
    },
    caraAI: {
      overline: "Cara AI · 工程智能",
      heading: "不是圖像生成工具。\n是珠寶工程 AI。",
      intro: "大多數 AI 只會生成漂亮的圖片。Cara 生成的設計能夠通過工廠的考驗，並且戴在手上感覺舒適。結果不是情緒板——而是附帶完整物料清單的可投產藍圖。",
      pillars: [
        {
          stat: "±0.15mm",
          label: "收縮補償",
          body: "Cara 在失蠟鑄造過程中計入金屬特定的收縮率——金、銀、鉑、黃銅——確保成品與數位模型一致，而非縮水的近似品。",
        },
        {
          stat: "0.6mm",
          label: "最小壁厚",
          body: "每個生成的造型都遵守合金特定的最小壁厚要求——鉑金0.6mm，金和銀0.8mm。沒有薄如紙的爪鑲，沒有塌陷的包鑲。設計在到達鑄造師手中之前就已結構穩固。",
        },
        {
          stat: "4-Prong+",
          label: "寶石公差映射",
          body: "Cara 為每種寶石類型和切割方式計算爪位、座深和腰圍間隙。鑲嵌經過工程設計，確保日常佩戴下的穩固固定——而非僅僅視覺上的合理性。",
        },
        {
          stat: "R≥0.4mm",
          label: "佩戴感與手感",
          body: "Cara 評估戒圈輪廓、內弧度、重量分佈和邊緣半徑，確保每件作品戴起來和看起來一樣好。尖銳邊緣、頭重腳輕的平衡和夾點在設計離開螢幕之前就會被標記。",
        },
      ],
      capabilities: [
        { label: "鑄造力學", detail: "澆口位置、閘口尺寸和流動分析，確保無缺陷鑄造" },
        { label: "結構分析", detail: "經過應力測試的壁厚和整體形態負載分佈檢查" },
        { label: "物料清單", detail: "從概念階段即提供準確的金屬重量、寶石數量和成本估算" },
        { label: "合金智能", detail: "10K–24K 金、925 銀、950 鉑金和黃銅的材料特性" },
        { label: "生產就緒", detail: "每個輸出都根據真實 EPH 製造公差進行評估" },
        { label: "草圖到 CAD 橋接", detail: "上傳餐巾紙草圖——Cara 提取幾何形狀並為生產進行工程設計" },
      ],
      closing: "Cara 工程化的概念旨在一次鑄造成功——無需返工、無浪費蠟模、無工廠退件。當買家看到 Cara 概念時，他們看到的是一件已經通過與製造商相同約束條件壓力測試的作品。這就是生成式 AI 與工程 AI 的區別。",
      tryCara: "試用 Cara AI — 即時演示",
      learnMore: "了解更多",
    },
    cta: {
      overline: "開始採購",
      heading: "瀏覽\n貿易目錄。",
      body: "每件設計連同 lookbook 攝影、產品圖及編輯故事一併交付——授權即刻可上架。瀏覽、以 3D 評估，比任何傳統流程更快推出市場。",
      primary: "瀏覽設計",
      secondary: "查看運作方式",
      tradeOnly: "僅限貿易帳戶",
    },
  },

  forManufacturers: {
    meta: {
      title: "生產 · Caratzon",
      description:
        "每份 Caratzon 訂單均於 EPH 生產——中國最大一站式珠寶製造基地。5,000+ 製造商、環保認證電鍍、由 CAD 到交付全程可追溯。",
    },
    hero: {
      overline: "生產 · EPH 製造基地",
      h1Line1: "一個基地。",
      h1Line2: "全鏈能力。",
      h1Line3: "5,000+ 製造商。",
      tagline:
        "每份 Caratzon 訂單均經 EPH 處理——中國最大一站式珠寶製造基地。5,000+ 製造商同一屋簷下，持牌環保電鍍，由 CAD 檔案到成品全程可追溯。",
      cta: "瀏覽目錄",
    },
    stats: {
      cadReady: "5,000+",
      everyOrder: "製造商同一屋簷下",
      phases: "全鏈覆蓋",
      milestone: "鑄造 · 鑲嵌 · 拋光 · 電鍍 · 品管",
      ecoPlating: "環保持牌",
      ephCertified: "廢水處理設施",
      fullTrace: "全程追蹤",
      clientVis: "蠟模到交付",
    },
    orderContents: {
      overline: "您的作品如何製作",
      subtitle: "由檔案到成品",
      items: [
        {
          heading: "CAD 到蠟模",
          body: "您的生產檔案送達 EPH，轉化為實體蠟模母版——由數碼設計到實物的第一步。",
        },
        {
          heading: "鑄造與鑲嵌",
          body: "失蠟鑄造、寶石鑲嵌及組裝在同一設施完成。無跨廠交接，公差零損失。",
        },
        {
          heading: "環保電鍍與修飾",
          body: "黃金、銠及合金電鍍於持牌環保產線完成，廠區設有廢水處理設施。拋光及修飾嚴格按規格執行。",
        },
        {
          heading: "品管與交付",
          body: "出貨前多點品質檢驗。生產里程碑於您的控制台即時可見，完成後簽發運單。",
        },
      ],
    },
    workflow: {
      overline: "生產階段",
      subtitle: "您的訂單，逐步推進",
      phases: [
        ["訂單確認", "您的設計進入 EPH 生產排程"],
        ["NRE 與試鑄", "蠟模完成，樣品提交審批"],
        ["鑄造", "金屬澆注，重量及合金驗證"],
        ["鑲嵌與組裝", "寶石鑲嵌、公差檢查、組件裝配"],
        ["電鍍與修飾", "環保認證電鍍按您的規格執行"],
        ["品管與發貨", "最終多點檢驗，簽發運單"],
      ],
    },
    eph: {
      overline: "EPH 製造基地",
      location: "中國",
      headline: "5,000+ 製造商。同一屋簷。完整供應鏈。",
      body: "EPH 是中國最大一站式珠寶製造基地——亦是每份 Caratzon 訂單的專屬生產設施。持牌廢水處理、環保認證電鍍產線，加上完整生產鏈同一屋簷下，EPH 為規模化可持續精品珠寶製造樹立了行業標杆。",
      attrs: [
        {
          label: "一站式供應鏈",
          body: "鑄造、鑲嵌、拋光及電鍍在同一設施完成。無跨廠物流，無交接遺失。",
        },
        {
          label: "持牌環保電鍍",
          body: "黃金、銠及合金表面處理於環保認證產線完成，廠區設有廢水處理設施——經驗證減少化學排放。",
        },
        {
          label: "規模與專業化",
          body: "5,000+ 製造商在各珠寶品類擁有深厚專業能力，涵蓋婚嫁系列至高級訂製。",
        },
        {
          label: "數碼直接對接",
          body: "CAD 檔案由 Caratzon 平台直接進入 EPH 生產排程——零人手重新輸入，全程可追溯。",
        },
      ],
    },
    cta: {
      overline: "瀏覽目錄",
      heading: "看看我們能\n生產甚麼。",
      primary: "瀏覽目錄",
      secondary: "申請成為製造商 →",
    },
    inquiryForm: {
      overline: "合作夥伴申請",
      heading: "申請加入\n製造網絡。",
      subtitle:
        "請告訴我們您的工廠及生產能力。合作團隊將審核您的申請並於 3 個工作天內回覆。",
      companyName: "公司 / 工廠名稱",
      contactName: "主要聯絡人姓名",
      email: "商務電郵",
      phone: "電話號碼",
      website: "公司網站",
      location: "工廠位置（城市、國家）",
      capabilities: "製造能力",
      capabilitiesHint: "選擇所有適用項",
      capabilityOptions: [
        "鑄造",
        "鑲嵌",
        "電鍍",
        "拋光",
        "CAD/CAM",
        "CNC",
        "裝配",
        "雕刻",
        "琺瑯",
        "品管 / 檢驗",
      ],
      specializations: "珠寶專業範疇",
      specializationsHint: "選擇所有適用項",
      specializationOptions: [
        "戒指",
        "耳環",
        "項鏈",
        "手鏈",
        "婚嫁系列",
        "男士系列",
        "腕錶",
        "訂製",
      ],
      certifications: "認證資質",
      certificationsHint: "選擇所有適用項",
      certificationOptions: [
        "RJC",
        "ISO 9001",
        "ISO 14001",
        "Hallmark",
        "Fairmined",
        "BSCI / Sedex",
      ],
      productionCapacity: "每月生產能力（件）",
      capacityOptions: [
        "500 件以下",
        "500 – 2,000",
        "2,000 – 10,000",
        "10,000 – 50,000",
        "50,000+",
      ],
      additionalInfo: "補充資料",
      additionalInfoPlaceholder:
        "其他您希望我們了解的資料——專長、交期、現有合作關係等。",
      submit: "提交申請",
      submitting: "提交中…",
      successHeading: "申請已收到",
      successBody:
        "感謝您有意加入 Caratzon 製造網絡。合作團隊將審核您的申請，並於 3 個工作天內與您聯繫。",
      errorRequired: "請填寫所有必填欄位。",
      errorEmail: "請輸入有效的電郵地址。",
      errorSend: "出現問題，請重試或直接電郵聯繫我們。",
    },
  },

  partnerApplication: {
    meta: {
      title: "合作夥伴申請 · Caratzon",
      description:
        "申請加入 Caratzon 製造網絡。告訴我們您的工廠、生產能力及認證資質。",
    },
  },

  contact: {
    overline: "聯絡我們",
    meta: {
      title: "聯絡我們 · Caratzon",
      description: "聯絡Caratzon團隊。與我們的貿易台對話、申請成為設計師或發送一般查詢。",
    },
    heading: "聯絡Caratzon",
    subtitle: "無論您是尋求獨家設計的零售品牌，還是希望將IP變現的3D CAD工程師，我們的全球團隊隨時為您服務。",
    tabs: {
      brand: "我是品牌/零售商",
      designer: "我是設計師",
      general: "一般/傳媒/投資者",
    },
    brand: {
      headline: "與貿易台對話",
      copy: "準備跳過研發、簡化製造流程？與我們的採購專家討論平台存取、大批量採購或EPH製造能力。",
      firstName: "名",
      lastName: "姓",
      workEmail: "工作電郵",
      companyName: "公司/品牌名稱",
      companyWebsite: "公司網站",
      helpWith: "我們能幫您什麼？",
      helpOptions: [
        "升級到Source / Atelier計劃",
        "大批量採購",
        "EPH製造能力",
        "Caratzon Studio AI示範",
        "平台一般問題",
      ] as readonly string[],
      selectPlaceholder: "選擇主題…",
      message: "留言",
      messagePlaceholder: "請告訴我們您的採購需求、時間線或具體問題…",
      submit: "發送訊息",
      submitting: "發送中…",
    },
    designer: {
      headline: "加入Caratzon設計師網絡",
      copy: "透過我們的全球授權市場將您的3D CAD珠寶設計變現。上傳作品集、設定條款，讓品牌主動找到您。",
      firstName: "名",
      lastName: "姓",
      email: "電郵地址",
      portfolioUrl: "作品集 / Behance / Instagram連結",
      cadSoftware: "主要CAD軟件",
      cadOptions: [
        "Rhino / Grasshopper",
        "Matrix / MatrixGold",
        "ZBrush",
        "Blender",
        "SolidWorks",
        "其他",
      ] as readonly string[],
      selectPlaceholder: "選擇您的主要工具…",
      designCount: "設計作品大致數量",
      designCountOptions: [
        "1–10",
        "11–50",
        "51–200",
        "200+",
      ] as readonly string[],
      designCountPlaceholder: "選擇範圍…",
      message: "介紹您的作品",
      messagePlaceholder: "描述您的設計風格、專業領域以及您對授權合作的期望…",
      submit: "申請成為設計師",
      submitting: "發送中…",
    },
    general: {
      headline: "一般查詢",
      copy: "傳媒、合作夥伴關係或投資者關係，請聯絡我們的企業團隊。",
      name: "姓名",
      email: "電郵地址",
      topic: "主題",
      topicOptions: [
        "傳媒與新聞",
        "投資者關係",
        "合作夥伴",
        "其他",
      ] as readonly string[],
      selectPlaceholder: "選擇主題…",
      message: "留言",
      messagePlaceholder: "我們能為您做什麼？",
      submit: "發送查詢",
      submitting: "發送中…",
    },
    success: {
      heading: "訊息已收到",
      body: "感謝您的來信。我們的團隊將在24小時內回覆您。",
      backToHome: "返回首頁",
      explorePlatform: "探索平台 →",
    },
    errors: {
      required: "請填寫所有必填項。",
      invalidEmail: "請輸入有效的電郵地址。",
      sendFailed: "出了點問題。請重試或直接發送電郵至hello@caratzon.com。",
    },
    offices: {
      heading: "全球辦事處",
      hongkong: { city: "香港", label: "亞太貿易台" },
      guangdong: { city: "廣東", label: "製造與營運總部" },
      london: { city: "倫敦", label: "歐洲貿易台" },
    },
  },

  about: {
    meta: {
      title: "關於 Caratzon — 我們的故事",
      description:
        "Caratzon 是連接獨立珠寶設計師與世界級製造的全球雙邊生態系統。了解我們如何革新珠寶供應鏈。",
    },
    hero: {
      overline: "關於 Caratzon · 成立於 2024 年",
      h1Line1: "卓越設計",
      h1Line2: "邂逅",
      h1Line3: "無瑕執行。",
      tagline:
        "Caratzon 源於一封幾乎令某珠寶品牌整個假日旺季泡湯的遺失電郵。今天，我們是連接才華洋溢的獨立設計師與世界級全程管理製造的全球雙邊生態系統。",
      lastUpdated: "最後更新：2026 年 2 月",
    },
    stats: {
      overline: "平台規模",
      subtitle: "數字說話",
      items: [
        { value: "10,000+", label: "獨家 CAD 設計" },
        { value: "150+", label: "嚴選製造夥伴" },
        { value: "1M+", label: "已協調生產節點" },
        { value: "$50M+", label: "為創作者產生的收入" },
        { value: "0", label: "黑盒供應鏈延誤" },
      ],
    },
    origin: {
      overline: "我們的起源",
      subtitle: "每個平台都有其創始故事",
      p1: "每一天，Caratzon 都在幫助創作者將藝術變現，同時讓 B2B 品牌採購新鮮、獨家的系列——以透明、節點化供應鏈的絕對確定性作為保障。",
      p2: "我們致力消除全球採購的風險與阻力。當品牌在我們平台上看中一件設計，他們毋須煩惱如何製造。Caratzon 全程處理 CAD 工程、實體打樣、多供應商物流、品質管控及最終交付——端對端一站管理。",
      digitalDesign: "數碼設計",
      precisionCraft: "精密工藝",
    },
    timeline: {
      overline: "Caratzon 的故事",
      subtitle: "四個篇章",
      phases: [
        {
          heading: "斷裂的供應鏈",
          subheading: "故事的起點",
          label: "問題所在",
          body: "一切始於一件設計精妙的多組件作品——需要訂製切割的藍寶石、精確的失蠟鑄造及專業的銠金表面處理。將 CAD 檔案分發至橫跨兩個大洲的三間工廠，卻因其中一間試圖隱瞞的細微氣孔問題而導致嚴重延誤。珠寶行業依賴 PDF 發票、WhatsApp 更新和盲目信任的現狀，顯然不可持續。",
        },
        {
          heading: "被困的才華",
          subheading: "一個隱藏在眼前的市場斷層",
          label: "核心洞察",
          body: "與此同時，我們發現市場存在巨大斷層。才華洋溢的獨立珠寶設計師坐擁滿硬碟的精彩 3D CAD，卻完全沒有工廠資源或資金去生產。而大型 B2B 零售品牌急需新鮮美學，卻對起用未知創作者的製造風險卻步。藝術與物流，從根本上背道而馳。",
        },
        {
          heading: "鍛造操作系統",
          subheading: "打造中樞神經系統",
          label: "建設之路",
          body: "我們決定建造終極橋樑。我們開發了一個專有、模組化、基於節點的平台，作為珠寶製造的中樞神經系統。它將設計師的構想直接連接至外部工廠，實時追蹤損益，並保護每一份技術規格——同時讓個別供應商完全互相隔離。",
        },
        {
          heading: "終極市集",
          subheading: "設計邂逅無瑕執行",
          label: "最終成果",
          body: "Caratzon 正式面世，不僅是一款追蹤工具，更是一個由全程管理製造引擎支撐的精選設計師市集。今天，當品牌在我們平台上看中一件設計，他們毋須煩惱如何製造。一鍵操作，Caratzon 自動將設計分配至嚴選網絡，處理 CAD 工程、實體打樣及最終品質管控。",
        },
      ],
    },
    vision: {
      overline: "願景與使命",
      visionLabel: "我們的願景",
      visionBody:
        "成為世界級珠寶設計與無瑕執行無縫交匯的權威全球生態系統——將碎片化的供應鏈轉化為對創作者和品牌均高度盈利的透明引擎。",
      missionLabel: "我們的使命",
      missionBody:
        "將獨立珠寶設計師的藝術與全球 B2B 品牌連接，賦能設計師——以我們全程管理的製造操作系統作為無縫後盾。我們的存在是為了消除全球採購的風險與阻力。",
    },
    howItWorks: {
      overline: "運作方式",
      subtitle: "一鍵操作。端對端完成。",
      steps: [
        {
          heading: "設計師上傳",
          body: "獨立設計師上傳 CAD 檔案、設定 IP 條款並發佈編輯故事。作品受帶簽名、有時效的瀏覽 URL 保護。",
        },
        {
          heading: "品牌發掘",
          body: "零售品牌在精選目錄中瀏覽 10,000+ 件獨家設計，透過平台內互動式 3D 預覽器評估作品。",
        },
        {
          heading: "Caratzon 分配",
          body: "達成協議後，我們的節點引擎自動將設計分配至嚴選製造網絡——CAD 工程、打樣、品管全程處理。",
        },
        {
          heading: "帶利潤交付",
          body: "實時損益可視化、里程碑追蹤及最終交付——零黑盒延誤，全程供應鏈完全透明。",
        },
      ],
    },
    leadership: {
      overline: "管理團隊",
      founderRole: "創辦人兼行政總裁",
      founderName: "[創辦人姓名]",
      founderBio:
        "請在此簡述您在珠寶設計、供應鏈管理及科技領域的背景——以及驅動 Caratzon 願景的動力。",
      tags: ["珠寶設計", "供應鏈", "科技"],
    },
    supplyChain: {
      overline: "供應鏈架構",
      heading: "節點化架構。\n完全透明。",
      items: [
        {
          title: "供應商完全隔離",
          desc: "個別工廠節點獨立運作。任何供應商均無法查看其他供應商的規格、定價或產能數據。",
        },
        {
          title: "實時損益追蹤",
          desc: "每個生產里程碑的利潤可視化——設計師、品牌及管理者只能看到與其角色相關的數據。",
        },
        {
          title: "不可篡改的審計追蹤",
          desc: "每個 CAD 版本、審批及生產事件均有時間戳記錄——完整的監管鏈，觸手可及。",
        },
      ],
    },
    quote: {
      text: "「藝術與物流，從根本上背道而馳。\n我們建造了這座橋。」",
      attribution: "Caratzon · 創辦理念",
    },
    cta: {
      overline: "與 Caratzon 合作",
      heading: "共建\n精品珠寶的未來。",
      primary: "瀏覽目錄",
      secondary1: "品牌及零售商 →",
      secondary2: "獨立設計師 →",
    },
  },

  forStudio: {
    meta: {
      title: "Caratzon Studio · Where AI Meets the Jeweler's Bench",
      description: "The AI-powered design workspace for jewelry creators. Meet Cara — your design assistant that thinks in millimeters, Mohs hardness, and tensile strength.",
    },
    hero: {
      overline: "Caratzon Studio",
      h1Line1: "Your Designs",
      h1Line2: "Deserve an Atelier.",
      tagline: "Caratzon Studio is the AI-powered workspace where independent jewelry designers upload CAD, tell their story, and let the platform handle everything else — marketing, licensing, production routing, and now, intelligent design assistance.",
      cta: "Apply as a Designer →",
      ctaSecondary: "See Pricing →",
    },
    stats: {
      aiAssisted: "AI-Assisted",
      designTools: "Design Tools",
      stoneTypes: "50+",
      stoneLabel: "Stone & Alloy Database",
      phases: "6",
      phasesLabel: "Sketch-to-Sales Steps",
      renders: "Instant",
      rendersLabel: "Catalog Renders",
    },
    cara: {
      overline: "Meet Cara",
      subtitle: "From Sketch to Sales",
      heading: "From Napkin Sketch\nTo Global Sales.",
      p1: "Cara is the AI design assistant that turns a hand-drawn sketch into a production-ready, market-listed piece of jewelry — handling every step from concept clarification to catalog renders, storytelling, and trade listing.",
      p2: "Snap a photo of your sketch. Cara asks the right questions — materials, dimensions, stone placement — then renders a faithful concept that respects your original vision down to every curve and proportion.",
      pipeline: [
        { num: "01", label: "Sketch", heading: "Upload Your Sketch", body: "Photograph a hand-drawn sketch or napkin doodle. Cara's vision model analyzes silhouettes, proportions, stone placements, and decorative details — extracting your intent with surgical precision.", icon: "pencil" },
        { num: "02", label: "Clarify", heading: "Intelligent Q&A", body: "Before touching a single pixel, Cara asks clarifying questions — metal type, stone cuts, ring size, finish preferences. Your answers become the design brief that locks in fidelity.", icon: "message-circle" },
        { num: "03", label: "Render", heading: "Faithful AI Render", body: "Cara generates a photorealistic concept that replicates your sketch with extreme fidelity — same silhouette, same proportions, same stone placement. Iterate through conversation until it's perfect.", icon: "image" },
        { num: "04", label: "Save", heading: "Save to Studio", body: "One click saves the approved render into your Studio as a new design draft — complete with the original sketch, conversation history, and all design parameters for future refinement.", icon: "save" },
        { num: "05", label: "Story", heading: "The Making Narrative", body: "Your sketch and the entire Cara conversation become the raw material for an AI-generated 'Making Of' story — a compelling editorial narrative that traces the creative journey from first line to final render.", icon: "book-open" },
        { num: "06", label: "Sell", heading: "List & Earn", body: "Catalog renders, lookbook assets, and trade listings are generated automatically. Your design enters the global catalog where brands discover, license, and order — and you earn 80% on every transaction.", icon: "trending-up" },
      ],
      conversationDemo: {
        label: "Cara — Sketch-to-Concept",
        userSketch: "I sketched this cocktail ring — can you turn it into a real concept?",
        caraAnalysis: "I can see a bold asymmetric band with what looks like a tension-set center stone and tapered shoulders. Before I render, let me clarify a few things:",
        caraQuestions: [
          "What metal are you envisioning? The angular lines suggest platinum or white gold",
          "The center stone — is that an emerald cut? Approximately what carat weight?",
          "I see texture marks on the band — brushed finish or hammered?",
        ],
        userReply: "Platinum, 2ct emerald-cut emerald, brushed finish on the band.",
        caraConfirm: "Perfect. Rendering your concept now — same silhouette, same proportions, platinum with brushed finish and a 2ct emerald-cut emerald in tension setting…",
        toolLabel: "Rendering faithful concept…",
      },
      cta: "Try Cara in Studio →",
    },
    storyLab: {
      overline: "AI Story Lab",
      subtitle: "Automated Storytelling",
      heading: "Your Story,\nTold Beautifully.",
      p1: "Answer seven guided questions during onboarding and our AI generates a rich editorial-style designer story, profile page, and lookbook — the kind of content that used to require a copywriter, photographer, and art director.",
      p2: "Each designer gets a dedicated Inspire profile where buyers discover your work through AI-generated narratives, not generic product listings.",
      features: [
        { label: "Editorial Profiles", description: "AI-generated designer stories that read like magazine features — not template bios." },
        { label: "AI Lookbooks", description: "Cohesive visual narratives across your portfolio, generated automatically from your designs." },
        { label: "Collection Storytelling", description: "Group pieces into themed collections and let AI weave a narrative across the set." },
      ],
    },
    workspace: {
      overline: "Designer Workspace",
      subtitle: "Everything in One Place",
      heading: "Upload. Manage.\nEarn.",
      p1: "The Studio workspace is your command center — upload CAD files, track licensing revenue, manage collections, monitor analytics, and communicate with buyers. All from one dashboard.",
      features: [
        { num: "01", heading: "3D Viewer", body: "Buyers inspect your designs in full 3D before licensing. Your CAD files are never exposed — only renders and previews." },
        { num: "02", heading: "Revenue Dashboard", body: "Track licensing fees, royalties, and production runs in real time. 80% revenue share on every license." },
        { num: "03", heading: "IP Protection", body: "CAD files are vault-encrypted and never shown to buyers. Structured licensing tiers with clear terms." },
        { num: "04", heading: "Direct Messaging", body: "Communicate with buyers and production managers directly inside the platform." },
      ],
    },
    revenue: {
      overline: "Revenue Models",
      subtitle: "Three Licensing Tiers",
      heading: "Earn on\nYour Terms.",
      models: [
        { tier: "standard", label: "Standard License", heading: "Non-Exclusive", description: "Multiple buyers can license the same design. You keep 80% of the licensing fee plus perpetual royalties on every production run.", benefit: "80% + Perpetual Royalties" },
        { tier: "embargo", label: "Seasonal Embargo", heading: "12-Month Exclusive", description: "One buyer gets competitor-proof exclusivity for a year. Higher upfront payment. After 12 months, the design returns to Standard availability.", benefit: "80% + Premium Exclusivity Fee" },
        { tier: "vault", label: "Full Exclusive", heading: "Permanent IP Transfer", description: "One-time buyout with permanent IP transfer including the CAD file. The highest upfront payment. Caratzon Kit included.", benefit: "Full Buyout + Caratzon Kit" },
      ],
    },
    steps: {
      overline: "Getting Started",
      subtitle: "Three Steps",
      items: [
        { heading: "Apply & Onboard", body: "Complete the seven-question onboarding flow. Our AI generates your editorial profile, designer story, and Inspire page automatically." },
        { heading: "Upload & Create", body: "Upload CAD files. Use Cara to refine designs, run structural analysis, and generate catalog-ready renders. Organize pieces into collections." },
        { heading: "Earn & Grow", body: "Buyers discover, license, and order your designs. You earn 80% on every license. Production is handled by our EPH-certified manufacturing hub." },
      ],
    },
    cta: {
      overline: "Join the Studio",
      heading: "Design the Future.\nWe Handle the Rest.",
      primary: "Apply as a Designer →",
      secondary: "Explore the Catalog →",
    },
  },

  studio: {
    nav: { guide: "指南", inbox: "收件匣", storyLab: "我的設計", collections: "作品系列", cara: "Cara", publishing: "發佈管理", admin: "管理後台 →", catalog: "← 目錄", analytics: "數據分析" },
    notifications: { heading: "通知", empty: "暫無通知。" },
    cara: {
      title: "Cara",
      subtitle: "AI設計助手",
      newSession: "新設計會話",
      placeholder: "描述您的願景，或上傳靈感圖片…",
      emptyTitle: "認識Cara",
      emptyDescription: "您的AI設計助手——集珠寶大師與機械工程師於一身。描述您的願景或上傳靈感圖片開始。",
      analyzing: "正在分析靈感…",
      rendering: "正在生成設計渲染…",
      structuralAnalysis: "正在運行可製造性模擬…",
      calculatingBom: "正在計算物料清單…",
      generatingVideo: "正在生成旋轉預覽——最多可能需要一分鐘…",
      processing: "處理中…",
      phases: { spark: "靈感", architect: "構造", crucible: "驗證", bench: "定稿" },
      artifacts: {
        title: "製品",
        empty: "Cara生成的製品將顯示在此處。",
        moodBoards: "情緒板",
        renders: "設計渲染",
        structuralAnalysis: "結構分析",
        materialAnalysis: "材料分析",
        bom: "物料清單",
        videos: "影片",
      },
      sessions: { empty: "暫無會話", emptyHint: "開始您與Cara的第一個設計會話", untitled: "未命名會話" },
      errors: { streamFailed: "出了點問題，請重試。", rateLimited: "訊息發送過快，請稍候。" },
    },
    gettingStarted: {
      overline: "設計師工作室",
      title: "入門指南",
      subtitle: "三大核心工作室功能的視覺化導覽——在開始之前了解完整流程。",
      howItWorks: {
        title: "運作方式",
        steps: [
          {
            title: "上傳與創建",
            label: "我的設計",
            body: "上傳您的 CAD 檔案及輔助素材，然後讓 AI 為您的設計生成可出版的目錄故事。",
          },
          {
            title: "回應請求",
            label: "收件匣",
            body: "品牌提交定製請求。您可以協商條款、交付修改版 CAD 檔案——一切均在同一對話中追蹤。",
          },
          {
            title: "策劃作品集",
            label: "作品集",
            body: "將已發佈的設計組織成主題式編輯作品集。AI 會將各個設計故事編織成連貫的敘事。",
          },
        ],
      },
      storyLabWorkflow: {
        title: "設計工作流程",
        subtitle: "兩個階段將您的 CAD 檔案轉化為已發佈的靈感頁面。",
        phase1: {
          title: "第一階段 — 設計草稿",
          subtitle: "五步嚮導收集平台在 AI 接手前所需的一切資訊。",
          steps: [
            { title: "身份", body: "為您的作品命名並撰寫簡短描述。" },
            { title: "定價與規格", body: "設定基礎授權價格、金屬重量和寶石數量。" },
            { title: "知識產權保護", body: "選擇標準授權、季節性獨家或完全獨家權利。" },
            { title: "上傳素材", body: "上傳生產 CAD、3D 預覽檔案和主圖渲染。" },
            { title: "審核與創建", body: "確認所有資訊並創建設計草稿。" },
          ],
        },
        phase2: {
          title: "第二階段 — AI 故事",
          subtitle: "六個步驟將您的草稿轉化為附有 AI 生成敘事和圖像的目錄故事。",
          steps: [
            { title: "靈感關鍵詞", body: "上傳參考照片；AI 分析情緒、材質和主題。" },
            { title: "選擇關鍵詞", body: "選擇最能捕捉作品靈魂的詞語。" },
            { title: "生成故事", body: "描述材質和設計意圖；AI 撰寫目錄敘事。" },
            { title: "修改故事", body: "編輯 AI 草稿——買家將閱讀這段文字。" },
            { title: "生成展示", body: "AI 構建包含章節、標題和圖像位的目錄版面。" },
            { title: "提交審批", body: "鎖定編輯並發送至審核團隊。" },
          ],
        },
      },
      afterSubmission: {
        title: "提交後會發生什麼",
        body: "管理員將審核您的故事和展示。您將收到決定通知——已批准的設計將在貿易目錄中上線，被退回的提交將附帶修改意見返回給您。",
        steps: [
          { label: "已提交", body: "您的設計進入審核隊列。" },
          { label: "已審核", body: "管理員評估故事質量和素材完整性。" },
          { label: "已發佈", body: "已批准的設計出現在貿易目錄中供品牌發現。" },
        ],
      },
      fileRequirements: {
        title: "檔案要求",
        subtitle: "接受格式和大小限制速查表。",
        columns: { asset: "素材", formats: "格式", maxSize: "大小上限", required: "必需" },
        rows: [
          { asset: "生產 CAD", formats: ".stl, .3dm, .step", maxSize: "50 MB", required: "是" },
          { asset: "3D 預覽", formats: ".glb", maxSize: "25 MB", required: "是" },
          { asset: "主圖渲染", formats: ".jpg, .png, .webp", maxSize: "10 MB", required: "是" },
          { asset: "參考照片", formats: ".jpg, .png, .webp", maxSize: "每張 5 MB", required: "可選" },
        ],
      },
      ipTiers: {
        title: "知識產權層級說明",
        subtitle: "三種授權模式——選擇適合您目標的方案。",
        tiers: [
          {
            title: "標準授權",
            body: "您的 CAD 由 Caratzon 保管，僅在保密協議下向認證製造商發放。您可獲得每筆授權費的 80% 加永久版稅。多個品牌可同時授權。",
          },
          {
            title: "季節性獨家",
            body: "一個品牌持有 12 個月的獨家權利。設計在其獨家期間隱藏，獨家期結束後重返市場。適用高級定價。",
          },
          {
            title: "完全獨家權利",
            body: "品牌永久獲得完整知識產權。您的生產 CAD 直接轉讓給他們。一次性高價值交易——設計永久從市場下架。",
          },
        ],
      },
      cta: {
        title: "準備好開始了嗎？",
        button: "創建您的第一個設計",
      },
    },
    storyLab: {
      overline: "設計師工作室",
      title: "我的設計",
      subtitle: "上傳設計、生成 AI 故事，並發佈到您的靈感頁面。",
      newDraft: "新建設計草稿",
      overlineLabel: "我的設計",
      cancel: "取消",
      continue: "繼續",
      back: "返回",
      stepOf: "第 {current} 步，共 {total} 步",
      keyboardHint: "提示：Alt + 方向鍵可切換步驟",
      editing: "編輯中…",
      draftInProgress: "草稿進行中",
      emptyTitle: "暫無設計",
      emptySubtitle: "上傳您的第一個設計，開始構建您的靈感作品集。",
      newDesign: "新建設計",
      emptyHint: "或從側邊欄選擇已有設計",
      batchUpload: "批量上傳",
      batchUploadSubtitle: "拖放最多 20 張珠寶照片——AI 為每件作品生成標題、描述和關鍵詞",
      batchProcessing: "正在處理 {count} 件作品…",
      batchGenerate: "生成 {count} 個設計",
      batchCreated: "已建立 {count} 個設計",
      batchDuplicates: "跳過 {count} 個重複項",
      batchPossibleDuplicates: "{count} 個可能重複",
      batchFailed: "{count} 個失敗",
      batchDuplicateExact: "已上傳為「{title}」",
      batchDuplicateNear: "可能與「{title}」相似（{confidence}% 匹配）",
      batchViewInLab: "在我的設計中查看",
      batchDropHere: "將最多 20 張珠寶照片拖放至此",
      batchDropHint: "AI 將為每件作品生成標題、描述和關鍵詞",
      celebrationTitle: "設計草稿已建立！",
      celebrationSubtitle: "您的設計已準備就緒。請從下方的關鍵詞生成開始構建其故事。",
      selectDesign: "請選擇一個設計\n以繼續",
      selectDesignHint: "從面板中選擇草稿，或建立新設計。",
      sidebar: { newDesign: "+ 新建設計", designs: "設計作品", noDesigns: "尚無設計" },
      steps: {
        identity: {
          title: "為作品命名",
          subtitle: "為您的設計取一個標題並撰寫簡短描述，捕捉其精髓。這是買家最先看到的內容。",
          designTitle: "設計標題",
          titleTip: "此標題將成為 Caratzon 市集上的公開名稱。請取一個引人入勝、令人難忘的名字——買家首先按標題瀏覽。",
          titlePlaceholder: "例如 Lunette Drop Earrings",
          titleHint: "選擇一個能捕捉作品精髓的名稱",
          description: "簡短描述",
          descriptionPlaceholder: "用一兩句話描述作品及其特色。",
          descriptionHint: "此描述將顯示在目錄中。請保持引人入勝且簡潔。",
        },
        pricing: {
          title: "定價與規格",
          subtitle: "設定基礎授權價格並提供材料規格。這些資訊有助於買家評估成本，並讓製造商準備生產報價。",
          basePrice: "基礎價格（美元）",
          basePriceTip: "這是您的授權費用。品牌商支付此金額以使用您的設計。扣除平台費用後，您將獲得 80%。",
          basePriceHint: "品牌商為使用您的設計而支付的授權費。您可獲得 80%。",
          metalWeight: "金屬重量（克）",
          metalWeightTip: "大約重量有助於製造商估算生產成本並提供準確報價。",
          metalWeightHint: "設計中金屬的大約重量",
          stoneCount: "寶石數量",
          stoneCountHint: "作品中鑲嵌的寶石總數",
          howPricingWorks: "定價機制說明：",
          howPricingBody: "您的基礎價格即設計授權費。製造成本由經審核的工廠根據金屬重量、寶石及工藝複雜度另行報價。",
        },
        licensing: {
          title: "選擇 IP 保護方案",
          subtitle: "選擇品牌商如何使用您的設計。每個層級提供不同的保護級別和收益模式。",
          licensingTip: "您的 IP 層級決定品牌商如何使用您的設計。標準授權允許多重授權；獨家期限授予單一品牌臨時專屬權利；全權轉讓則完整轉移所有權。",
          standard: "標準授權",
          standardDesc: "您的 CAD 檔案由 Caratzon 保管，僅在 NDA 保護下提供給經審核的製造商。您可獲得每筆授權費的 80% 及持續版稅。",
          standardDetail: "最適合希望獲得持續被動收入的設計師。多個品牌商可同時獲得授權。",
          exclusive: "季節性獨家期",
          exclusiveDesc: "單一品牌商獲得 12 個月的獨家使用權。獨家期內設計將被隱藏。獨家期結束後重新上架。",
          exclusiveDetail: "以溢價獲得獨家權利。品牌商在獨家期內支付更高的授權費用。",
          vault: "全權獨家轉讓",
          vaultDesc: "品牌商永久獲得完整 IP。您的生產 CAD 檔案將直接轉讓給對方。您可獲得收購價格的 80%。",
          vaultDetail: "一次性高價值交易。設計將從市集永久下架。",
          ipTransfer: "IP 轉讓",
        },
        assets: {
          title: "上傳您的資產檔案",
          subtitle: "上傳買家和製造商所需的設計檔案。您的生產 CAD 受到保護——僅經審核的製造商可在 NDA 下查閱。",
          assetsTip: "您的生產 CAD 絕不會與買家分享。僅 Caratzon 審核通過的製造商可在嚴格 NDA 條款下存取。",
          requiredFiles: "必需檔案",
          optionalFiles: "選填——可提升 AI 故事品質",
          vaultNotice: "全權獨家轉讓。",
          vaultNoticeDetail: "您的 CAD 檔案將直接轉讓給收購品牌商。",
          cadProtected: "CAD 受 IP 保護。",
          cadProtectedDetail: "僅 Caratzon 審核通過的製造商可在 NDA 下獲取。",
          cadLabel: "生產 CAD",
          cadLabelVault: "用於 IP 轉讓的生產 CAD",
          cadHint: ".stl · .3dm · .step — 您的原始檔案，絕不向買家披露",
          viewerLabel: "3D 預覽資產",
          viewerHint: ".glb — 從您的 CAD 匯出的互動式買家預覽檔案",
          renderLabel: "2D 渲染 / 主圖",
          renderHint: ".jpg · .png · .webp — 目錄的主要縮圖",
        },
        review: {
          title: "審閱並建立",
          submit: "建立設計草稿",
          submitting: "建立中…",
          identity: "作品資訊",
          pricingSpecs: "定價與規格",
          licensingTier: "授權層級",
          assets: "資產檔案",
          edit: "編輯",
          uploaded: "已上傳",
          missing: "未上傳",
          allRequired: "建立前須上傳所有必需檔案。",
        },
      },
      workflow: {
        featured: "精選",
        revisionRequired: "需要修訂",
        revisionHint: "請根據上方的反饋進行修改，然後重新提交。",
        noReason: "未提供原因。",
        step01: "靈感關鍵詞",
        step01Sub: "上傳參考照片並添加背景資訊。AI 分析情緒、材質和主題，建立關鍵詞庫。",
        additionalContext: "補充背景資訊",
        contextPlaceholder: "例如 Art Deco 風格影響、午夜藍藍寶石…",
        generateKeywords: "生成關鍵詞",
        regenerateKeywords: "重新生成關鍵詞",
        analysingRefs: "正在分析參考圖…",
        step02: "選擇關鍵詞",
        step02Sub: "選擇最能捕捉這件作品靈魂的詞彙——它們將成為 AI 生成故事的錨點。",
        saveKeywords: "儲存已選關鍵詞",
        saving: "儲存中…",
        step03: "生成故事",
        step03Sub: "描述材質、設計意圖和隱含寓意。AI 將撰寫一篇在您的目錄頁面上展示的敘事文。",
        pieceDescription: "作品描述",
        describePlaceholder: "描述此設計",
        materialsColors: "材質與色彩",
        materialsPlaceholder: "純銀、密鑲鑽石…",
        initialSpark: "創作靈感",
        sparkPlaceholder: "一幅畫面、一段記憶或一種氛圍…",
        hiddenSymbolism: "隱含寓意",
        symbolismPlaceholder: "這件作品中蘊含了什麼寓意？",
        generateStory: "生成故事",
        regenerateStory: "重新生成故事",
        writingStory: "正在撰寫您的故事…",
        step04: "精煉故事",
        step04Sub: "審閱 AI 草稿。自由編輯標題和正文——這段文字將展示在目錄頁面，供買家閱讀。",
        saveStory: "儲存故事",
        step05: "生成展示頁面",
        step05Sub: "AI 構建目錄展示版面——區塊數量、圖片位置和標題。全部從您的故事中生成。",
        currentLayout: "目前版面：",
        sections: "個區塊",
        generatedImages: "張生成圖片",
        generatedImagesPlural: "張生成圖片",
        regenerateWarning: "重新生成將替換目前的版面。",
        generateShowcase: "生成展示頁面",
        regenerateShowcase: "重新生成展示頁面",
        buildingShowcase: "正在構建展示頁面…",
        step06: "提交審批",
        step06Sub: "鎖定進一步編輯，直到管理員審閱您的故事和展示頁面。審批結果將通知您。",
        awaitingReview: "您的設計正在審閱團隊處理中。審批完成後您將收到通知。",
        submitForApproval: "提交審批",
        awaitingAdmin: "等待管理員審閱…",
        published: "已發佈",
        submitting: "提交中…",
        viewPublished: "查看已發佈的故事",
      },
      upload: {
        dragOrClick: "拖放至此或",
        clickToBrowse: "點擊瀏覽",
        maxSize: "最大 {size} MB",
        required: "必需",
        uploading: "正在上傳 {name}…",
        replace: "替換",
        retry: "重試",
        demoPreLoaded: "示範檔案已預載",
        photoRefs: "參考照片",
        photoRefsHint: "——選填，可提升 AI 品質",
        dragOrClickPhotos: "拖放或點擊以添加參考照片",
        upToPhotos: "最多 {max} 張照片",
        remove: "移除",
        inspirationNotes: "靈感筆記",
        inspirationPlaceholder: "一段記憶、一種氛圍、一份情感暗示或某個特定的靈感火花。",
      },
    },
    collections: {
      overline: "設計師工作室",
      title: "作品集",
      subtitle: "將已發佈的設計組合成策劃系列，配以 AI 生成的編輯故事。",
      newCollection: "新建作品集",
      emptyTitle: "您的第一個作品集",
      emptySubtitle: "選擇兩件或以上已發佈的設計來創建作品集。AI將編織它們的故事。",
      emptyHint: "或從側欄選擇現有作品集",
      celebrationTitle: "作品集已創建！",
      celebrationSubtitle: "您的作品集已準備就緒。從下方的關鍵詞生成開始。",
      sidebar: { collections: "作品集", noCollections: "尚無作品集" },
      designPicker: {
        title: "作品集作品",
        subtitle: "選擇要包含的已發佈設計。您至少需要2件設計。",
        selected: "已選 {count} 件",
        minRequired: "請至少選擇2件設計",
        saveDesigns: "保存作品",
        saving: "保存中…",
      },
      create: {
        title: "創建作品集",
        collectionTitle: "作品集標題",
        titlePlaceholder: "例如：午夜海洋系列",
        description: "簡短描述",
        descriptionPlaceholder: "幾句關於作品集主題的描述。",
        selectDesigns: "選擇設計",
        selectDesignsHint: "從已發佈的設計中選擇至少2件",
        inspirationNote: "靈感筆記",
        inspirationNotePlaceholder: "什麼將這些作品聯繫在一起？",
        submit: "創建作品集",
        submitting: "創建中…",
      },
      draftNotInitialized: "作品集草稿尚未初始化。",
      draftNotInitializedCta: "返回作品集",
      workflow: {
        step00: "作品集作品",
        step00Sub: "管理此作品集中包含的設計。",
        step01: "作品集關鍵詞",
        step01Sub: "AI分析所有作品，尋找統一的主題、圖案和氛圍。",
        additionalContext: "附加上下文",
        contextPlaceholder: "例如：共同的創作季節、材料色調、情感主線…",
        generateKeywords: "生成關鍵詞",
        regenerateKeywords: "重新生成關鍵詞",
        analysingCollection: "分析作品集中…",
        step02: "選擇關鍵詞",
        step02Sub: "選擇最能捕捉這個作品集靈魂的詞彙。",
        saveKeywords: "保存選定關鍵詞",
        saving: "保存中…",
        step03: "生成作品集故事",
        step03Sub: "AI將各設計故事編織成連貫的作品集敘事。",
        generateStory: "生成故事",
        regenerateStory: "重新生成故事",
        writingStory: "撰寫作品集故事中…",
        step04: "修改故事",
        step04Sub: "審閱並編輯生成的作品集敘事。",
        saveStory: "保存故事",
        step05: "生成展示頁面",
        step05Sub: "AI為每個版塊設計多作品編輯展示頁面及圖片。",
        currentLayout: "當前版面：",
        sections: "個版塊",
        generatedImages: "張生成圖片",
        generatedImagesPlural: "張生成圖片",
        regenerateWarning: "重新生成將替換當前版面。",
        generateShowcase: "生成展示頁面",
        regenerateShowcase: "重新生成展示頁面",
        buildingShowcase: "構建展示頁面中…",
        step06: "提交審核",
        step06Sub: "鎖定進一步編輯，直到管理員審核您的作品集故事和展示頁面。",
        awaitingReview: "您的作品集已提交審核團隊。決定做出後您將收到通知。",
        submitForApproval: "提交審核",
        awaitingAdmin: "等待管理員審核…",
        published: "已發佈",
        submitting: "提交中…",
        viewPublished: "查看已發佈的作品集",
        revisionRequired: "需要修改",
        revisionHint: "根據以上反饋進行修改，然後重新提交。",
        noReason: "未提供原因。",
        featured: "精選",
      },
    },
    inbox: {
      title: "定製收件匣",
      subtitle: "洽談條款、審閱需求說明，並上傳修改版 CAD 檔案。",
      overline: "設計師工作室",
      openRequests: "待處理請求",
      emptyTitle: "暫無活躍請求",
      emptySubtitle: "當品牌為您的設計提交定製請求時，它將顯示在這裡。",
      selectRequest: "從側邊欄選擇一個請求以開始。",
      thread: "對話記錄",
      noMessages: "暫無消息。",
      updateStatus: "更新狀態",
      uploadRevision: "上傳 CAD 修改版",
      revisionHint: "將請求狀態移至「進行中」以啟用 CAD 修改版上傳。",
    },
    analytics: {
      overline: "工作室",
      title: "設計數據分析",
      subtitle: "追蹤您的設計表現 — 瀏覽量、互動率及受眾觸及率。",
      cards: {
        totalViews: "總瀏覽量",
        uniqueVisitors: "獨立訪客",
        avgTimeOnPage: "平均停留時間",
        quoteRequests: "報價請求",
      },
      table: {
        design: "設計",
        views: "瀏覽量",
        uniqueViews: "獨立訪客",
        clicks: "點擊數",
        avgTime: "平均時長",
        scrollDepth: "捲動深度",
      },
      drillDown: {
        viewsTrend: "瀏覽趨勢（30 天）",
        clickBreakdown: "點擊分佈",
        referrerBreakdown: "流量來源",
      },
      empty: "暫無數據分析。當您的設計發佈並獲得瀏覽後，指標將在此顯示。",
      seconds: "秒",
    },
  },

  catalog: {
    overline: "貿易目錄",
    resultCount: "{count} 個結果",
    designCount: "{count} 件設計",
    emptyTitle: "目錄正在精選策劃中",
    emptySubtitle: "設計師正在準備首批作品集。請稍後再來。",
    searchEmptyTitle: "未找到匹配結果",
    searchEmptySubtitle: "嘗試不同的搜索詞或瀏覽所有設計。",
    browseCta: "瀏覽所有設計",
  },

  orders: {
    overline: "貿易工作區",
    title: "採購訂單",
    subtitle: "追蹤進行中的洽談、審閱設計師提交的 3D 修改版，並批准提交進入生產路由。",
    activeOrders: "進行中訂單",
    emptyTitle: "尚無採購訂單",
    emptyCta: "瀏覽貿易目錄 →",
    selectOrder: "選擇一個訂單以查看洽談記錄。",
    latestRevision: "最新 CAD 修改版 — 3D 預覽",
    awaitingRevision: "等待設計師提交首份 CAD 修改版。",
    respondToDesigner: "回覆設計師",
  },

  admin: {
    brand: "管理後台",
    nav: { workflows: "工作流程", publishing: "發佈管理", users: "用戶管理", academy: "學院", developer: "Developer", queues: "隊列監控", catalog: "目錄", studio: "工作室", analytics: "數據分析", catalogAdmin: "設計目錄", catalogListings: "貿易上架", inspireAdmin: "靈感展示", storyLab: "我的設計" },
    users: {
      overline: "管理後台",
      title: "用戶管理",
      subtitle: "建立、編輯及管理所有角色的用戶帳戶。",
      tabs: { users: "用戶", createNew: "新建帳戶" },
      stats: { total: "用戶總數", active: "活躍", suspended: "已停權", designers: "設計師" },
      search: "按姓名或電郵搜尋…",
      allRoles: "所有角色",
      noMatch: "沒有符合篩選條件的用戶。",
      columns: { name: "姓名", email: "電郵", role: "角色", status: "狀態", actions: "操作" },
      statusActive: "活躍",
      statusSuspended: "已停權",
      edit: "編輯",
      suspend: "停權",
      reactivate: "恢復",
      roles: {
        DESIGNER: "設計師",
        B2B_BUYER: "品牌商 / 買家",
        MANUFACTURER: "製造商",
        PRODUCTION_MANAGER: "生產經理",
        ADMIN: "管理員",
      },
      roleLabels: { DESIGNER: "設計師", B2B_BUYER: "品牌商", MANUFACTURER: "工廠", PRODUCTION_MANAGER: "生產經理", ADMIN: "管理員" },
      create: {
        title: "建立帳戶",
        subtitle: "新增設計師、買家、製造商或內部員工帳戶。",
        name: "全名",
        namePlaceholder: "例如 Aria Atelier",
        email: "電郵地址",
        emailPlaceholder: "designer@example.com",
        password: "密碼",
        passwordPlaceholder: "最少 8 個字符",
        role: "角色",
        submit: "建立帳戶",
      },
      invite: {
        title: "發送邀請連結",
        subtitle: "為指定電郵及角色生成註冊連結。有效期為 7 天。",
        email: "電郵",
        emailPlaceholder: "new-designer@example.com",
        role: "角色",
        submit: "生成連結",
        copied: "已複製！",
        copy: "複製",
      },
      editDialog: {
        title: "編輯用戶",
        subtitle: "更新帳戶資料及角色分配。",
        name: "姓名",
        email: "電郵",
        role: "角色",
        save: "儲存更改",
        cancel: "取消",
        stripe: "Stripe",
        accountActions: "帳戶操作",
        resetPassword: "重設密碼",
        deleteAccount: "刪除帳戶",
      },
      confirm: {
        suspendTitle: "停權用戶",
        suspendDesc: "確認停權 {name}？該用戶將立即失去平台存取權限。",
        suspendButton: "確認停權",
        reactivateTitle: "恢復用戶",
        reactivateDesc: "確認恢復 {name}？該用戶將重新獲得完整存取權限。",
        reactivateButton: "確認恢復",
        deleteTitle: "刪除用戶",
        deleteDesc: "確認永久刪除 {name}？此操作無法撤銷，所有相關資料將被移除。",
        deleteButton: "永久刪除",
        cancel: "取消",
      },
      success: {
        created: "用戶帳戶已成功建立。",
        updated: "用戶資料已更新。",
        suspended: "用戶帳戶已停權。",
        reactivated: "用戶帳戶已恢復。",
        deleted: "用戶帳戶已刪除。",
        reset: "臨時密碼已生成。",
        invited: "邀請連結已生成。",
      },
      tempPasswordHint: "請立即複製——此密碼不會再次顯示。",
      dismiss: "關閉",
    },
    setup: {
      title: "平台設定",
      subtitle: "建立首個管理員帳戶以開始使用。",
      steps: { authorize: "授權", credentials: "帳戶資料", designer: "設計師", confirm: "確認" },
      setupPasswordLabel: "設定密碼",
      setupPasswordPlaceholder: "輸入設定密碼",
      setupPasswordHint: "此實例需要設定密碼才能建立首個管理員帳戶。",
      setupPasswordInvalid: "設定密碼不正確。",
      emailLabel: "電郵地址",
      emailPlaceholder: "admin@company.com",
      nameLabel: "全名",
      namePlaceholder: "陳大文",
      passwordLabel: "密碼",
      passwordPlaceholder: "最少 8 個字元",
      confirmPasswordLabel: "確認密碼",
      confirmPasswordPlaceholder: "重新輸入密碼",
      designerTitle: "設計師帳戶",
      designerSubtitle: "建立首個設計師帳戶，讓其開始上傳設計。",
      designerNameLabel: "設計師名稱",
      designerNamePlaceholder: "Aria Atelier",
      designerEmailLabel: "設計師電郵",
      designerEmailPlaceholder: "designer@company.com",
      designerPasswordLabel: "設計師密碼",
      designerPasswordPlaceholder: "最少 8 個字元",
      reviewTitle: "檢視",
      reviewSubtitle: "確認將建立的帳戶。",
      createAccount: "建立帳戶",
      back: "返回",
      next: "下一步",
      passwordMismatch: "密碼不一致。",
      passwordTooShort: "密碼最少需要 8 個字元。",
    },
    analytics: {
      overline: "管理後台",
      title: "平台數據分析",
      subtitle: "全平台設計互動指標及成效分析。",
      cards: {
        totalViews: "總瀏覽量",
        uniqueVisitors: "獨立訪客",
        avgTimeOnPage: "平均停留時間",
        totalClicks: "總點擊數",
      },
      filters: {
        designer: "按設計師篩選",
        dateRange: "日期範圍",
        allDesigners: "所有設計師",
      },
      leaderboard: "表現最佳設計",
      zeroEngagement: "需要關注（零互動）",
      table: {
        design: "設計",
        designer: "設計師",
        views: "瀏覽量",
        uniqueViews: "獨立訪客",
        clicks: "點擊數",
        avgTime: "平均時長",
        scrollDepth: "滾動深度",
      },
      drillDown: {
        viewsTrend: "瀏覽趨勢",
        clickBreakdown: "點擊分佈",
        referrerBreakdown: "流量來源",
      },
      empty: "暫無數據分析。當設計發佈並獲得瀏覽後，指標將在此顯示。",
      seconds: "秒",
    },
  },

  messages: {
    overline: "通訊",
    title: "訊息",
    subtitle: "與設計師、生產經理及製造合作夥伴的對話。",
    emptyTitle: "暫無對話",
    emptyCta: "瀏覽目錄以開始",
    typeMessage: "輸入訊息...",
    send: "傳送",
    translate: "翻譯",
    translating: "翻譯中...",
    loadMore: "載入更早訊息",
    typing: "正在輸入...",
    typingMultiple: "正在輸入...",
    today: "今天",
    yesterday: "昨天",
    unread: "未讀",
    newConversation: "新對話",
    participants: "參與者",
    quoteProposal: "報價方案",
    statusUpdate: "狀態更新",
    attachFile: "附加檔案",
    accept: "接受",
    counter: "還價",
    connecting: "正在連接聊天...",
    estimatedDays: "預計交付",
    days: "天",
    messageDesigner: "聯繫設計師",
  },

  pricing: {
    meta: {
      title: "定價 · Caratzon",
      description: "Caratzon 品牌透明定價——採購預工程珠寶設計、生成 AI 行銷套件、可持續製造。",
    },
    hero: {
      overline: "品牌專屬 · 貿易會員",
      heading: "別再為每個設計支付 $2,500。",
      headingEm: "從 $0 開始。",
      body: "您的會員資格解鎖完整目錄、AI 驅動的行銷工具，以及與獨立設計師的直接採購。設計授權另計——您只需為選擇生產的設計付費。",
    },
    socialProof: [
      "48+ 預工程設計",
      "來自 6 個國家的設計師",
      "AI 套件 3 分鐘內生成",
      "首件即盈利",
    ],
    billing: {
      monthly: "月付",
      annual: "年付",
      savingActive: "節省 ~17%",
      savingPrompt: "年付節省 ~17%",
    },
    tiers: {
      explorer: {
        name: "Discover",
        tagline: "探索可能",
        description: "瀏覽目錄、收藏設計，獲得 200 迎新積分生成您的首個 AI 行銷套件——免費，無需信用卡。",
        features: [
          "瀏覽完整目錄——每位設計師、每件作品",
          "收藏設計至您的候選清單",
          "200 迎新 Studio Credits（一次性）",
          "生成 2 個 Essentials Kit 親身體驗品質",
          "選擇數量——我們處理其餘，從 CAD 到交付",
        ],
        cta: "免費開始",
      },
      professional: {
        name: "Source",
        tagline: "直接接觸設計師",
        description: "完整採購權限。直接設計師關係。每月 1,500 積分——足夠 15 套完整行銷套件或 150 張快速目錄照片。",
        mostPopular: "最受歡迎",
        features: [
          "Discover 的所有功能，加上：",
          "每月 1,500 Studio Credits",
          "直接從設計師採購——無中間商",
          "通過私訊協商授權條款",
          "AI 行銷套件：生活照、造型冊、社交素材——數分鐘完成",
          "Standard + Exclusive 授權層級",
          "按金屬、寶石、風格、價格和供應篩選",
        ],
        cta: "開始 Source",
      },
      atelier: {
        name: "Atelier",
        tagline: "您的私人設計工坊",
        description: "通過 Vault 授權實現完整知識產權轉讓。您的專屬製作經理處理從 CAD 到交付的一切。每月 5,000 積分。",
        features: [
          "Source 的所有功能，加上：",
          "Vault 授權——完整 IP + CAD 檔案轉讓",
          "每月 5,000 Studio Credits",
          "專屬製作經理——您的單一聯絡人",
          "優先支援——直線聯繫、當日回覆",
          "搶先接觸新設計師",
          "自訂採購簡報——告訴我們您需要什麼，我們來找",
          "多 SKU 訂單的批量授權條款",
          "Cara AI——以 AI 輔助設計原創珠寶",
        ],
        cta: "開始 Atelier",
      },
      enterprise: {
        name: "Enterprise",
        tagline: "無限擴展",
        description: "適用於零售連鎖店和大型分銷商。自訂積分額度、API 整合、白手套製作管理和專屬客戶團隊。",
        customLabel: "自訂",
        customSublabel: "根據您的規模和需求量身定制",
        features: [
          "Atelier 的所有功能，加上：",
          "自訂 Studio Credit 額度——匹配您的目錄速度",
          "API 存取——目錄、套件和訂單管理",
          "跨多家工廠的白手套製作管理",
          "專屬客戶團隊，季度業務回顧",
          "自訂 SLA、入職和培訓",
          "多席位團隊存取，基於角色的權限",
          "協商授權條款和批量折扣",
          "優先製造排隊——您的訂單優先出貨",
        ],
        cta: "聯繫我們的團隊",
      },
    },
    priceDisplay: {
      free: "免費",
      freeSublabel: "無需信用卡",
      perMonth: "/月",
      saveBadge: "（節省 ~17%）",
      orAnnualOption: "或 {amount}/年",
      billedAnnually: "年付 {amount}",
      licensesSeparate: "設計授權另計",
    },
    getStarted: "立即開始",
    studioCredits: {
      overline: "Studio Credits",
      heading: "固定成本，每次行動。無意外。",
      body: "每項 AI 操作——從目錄攝影到與 Cara 的設計諮詢——都從您的每月配額中扣除固定數量的積分。需要更多？充值包即時可用，永不過期。",
      link: "查看積分費用和充值包 →",
    },
    roi: {
      overline: "數據說話",
      heading: "推出新 SKU 的真正成本。",
      body: "傳統採購將利潤埋在隱藏成本和數月的交期中。Caratzon 壓縮整個流程。",
      traditionalLabel: "傳統採購",
      caratzonLabel: "Caratzon",
      rows: [
        { metric: "上市時間", traditional: "4–6 個月", caratzon: "3 週" },
        { metric: "研發與 CAD 建模", traditional: "$800 – $2,500+", caratzon: "$0——預工程" },
        { metric: "實物打樣", traditional: "每輪 $500+", caratzon: "$0——AI 渲染預覽" },
        { metric: "行銷攝影", traditional: "每次活動 $2,000+", caratzon: "約 $7（Studio Credits）" },
        { metric: "損益平衡點", traditional: "約售出 50 件", caratzon: "第 1 件" },
      ],
      note: "預工程 CAD 檔案。數碼孿生打樣。AI 驅動攝影。Caratzon 上的每個設計從第一件起即盈利。",
    },
    faq: {
      overline: "常見問題",
      heading: "您需要知道的一切。",
      items: [
        {
          q: "平台費用和設計授權分別包含什麼？",
          a: "您的會員資格提供目錄存取、AI 驅動行銷工具（Studio Credits）和直接採購能力。設計授權另計——您只需為選擇生產的設計付費。會員資格是您的工具箱；授權是材料。",
        },
        {
          q: "未使用的 Studio Credits 會怎樣？",
          a: "月度積分在每個計費週期結束時過期。這確保定價對所有人公平。如果您經常需要更多，升級計劃或購買充值包——充值積分永不過期。",
        },
        {
          q: "可以隨時切換計劃或取消嗎？",
          a: "可以。即時升級，在計費週期結束時降級。無鎖定合約，無取消費用。年度計劃可取消，未使用月份按比例退款。",
        },
        {
          q: "設計授權具體包含什麼？",
          a: "每個授權包含預工程 CAD 檔案（直接路由至生產——您無需處理檔案）、所選層級的完整商業權利（Standard、Exclusive 或 Vault），以及 Essentials Kit（目錄照片、規格、SEO 文案）。帶生活攝影的 Caratzon Kit 使用您的 Studio Credits 生成。",
        },
        {
          q: "Vault 授權（IP 轉讓）如何運作？",
          a: "適用於 Atelier 和 Enterprise 計劃。Vault 授予您設計的永久獨家所有權——包括原始 CAD 檔案。設計師收到一次性買斷費。一旦入庫，設計將從公開目錄中移除。它是您的。",
        },
        {
          q: "我需要自己處理製造嗎？",
          a: "不需要。每個生產訂單通過我們審核的 EPH 製造網絡路由。鑄造、鑲嵌、品質管控和物流端對端管理。您從儀表板批准里程碑——我們處理其餘。",
        },
      ],
    },
    bottomCta: {
      heading: "準備好以不同方式採購了嗎？",
      body: "從 Discover 免費開始。準備好時升級——無承諾。",
      button: "瀏覽目錄",
      link: "需要自訂計劃？聯繫我們。",
    },
  },

  ai: {
    meta: {
      title: "Cara AI — 珠寶工程智能 · Caratzon",
      description: "Cara 是為工廠車間設計的珠寶工程 AI。收縮補償、結構分析、物料清單和生產就緒概念——不是幻想渲染。",
      socialDescription: "為真實製造而工程的 AI。收縮、公差、鑄造力學、佩戴性——每個概念都是生產就緒的。",
    },
    hero: {
      headingLine1: "不是生成式圖像工具。",
      headingLine2: "珠寶工程 AI。",
      ctaDemo: "試用 Cara AI — 即時演示",
      ctaCredits: "查看積分費用",
    },
    pillars: {
      label: "工程約束",
      headingLine1: "每個概念都經過壓力測試",
      headingLine2: "在離開螢幕之前。",
    },
    capabilities: {
      label: "能力",
      headingLine1: "從餐巾草圖",
      headingLine2: "到生產就緒藍圖。",
    },
    whatYouCanAsk: {
      label: "您可以問什麼",
      heading: "對話，不是提示框。",
      groups: [
        {
          title: "設計諮詢",
          prompts: [
            "「分析這個草圖的可製造性」",
            "「18K 白金的最小壁厚是多少？」",
            "「這個光環鑲嵌能承受日常佩戴嗎？」",
          ],
        },
        {
          title: "材料科學",
          prompts: [
            "「比較鉑金 950 和 18K 在這個戒圈上的表現」",
            "「0.8mm 爪鑲應該用什麼合金？」",
            "「14K 黃金失蠟鑄造的收縮率是多少？」",
          ],
        },
        {
          title: "視覺生成",
          prompts: [
            "「用玫瑰金和密鑲戒圈渲染這個概念」",
            "「生成 Art Deco 訂婚戒指的情緒板」",
            "「創建這個設計的 360° 旋轉影片」",
          ],
        },
        {
          title: "生產規劃",
          prompts: [
            "「為這枚戒指生成物料清單」",
            "「估算 18K 配 VS1 鑽石的製造成本」",
            "「50 件批量的生產時間表是什麼？」",
          ],
        },
      ],
    },
    creditsTeaser: {
      label: "Studio Credits",
      heading: "透明、固定成本的 AI。",
      body: "每個 Cara 操作都有固定積分費用——5 積分一條訊息、15 積分一張圖像渲染、25 積分一段 360° 影片。無 token 計量，無意外。",
      link: "查看所有積分費用和充值包 →",
    },
    finalCta: {
      heading: "親自體驗。",
      body: "試用即時演示——無需帳戶。上傳草圖、提出問題，看 Cara 工程出生產就緒的概念。",
      ctaDemo: "試用 Cara AI — 即時演示",
      ctaPricing: "查看定價",
    },
  },

  designerAi: {
    meta: {
      title: "Cara AI 設計師專用 — Caratzon",
      description: "驗證 CAD 可製造性、生成作品集渲染圖、360° 影片及造型攝影 — 全由 Cara AI 驅動。專注設計，讓 Cara 處理工程細節。",
      socialDescription: "珠寶設計師的 AI 拍檔。驗證 CAD、生成渲染、打造作品集 — 從第一天起即可量產。",
    },
    hero: {
      overline: "Cara AI · 設計師專用",
      headingLine1: "放膽設計。",
      headingLine2: "Cara 處理工程。",
      body: "Cara 是您的 AI 拍檔 — 集珠寶大師與機械工程師於一身。驗證 CAD 可製造性、生成專業級渲染圖，打造讓買家主動聯繫的作品集。您只需專注創作。",
      ctaDemo: "試用 Cara AI — 即時示範",
      ctaStudio: "開啟 Studio",
    },
    pillars: {
      label: "Cara 為您做什麼",
      headingLine1: "從 CAD 檔案",
      headingLine2: "到可量產的作品集。",
      items: [
        {
          stat: "CAD → 渲染",
          label: "作品集生成",
          body: "上傳 .3DM 或 .STL，Cara 即生成專業級產品照、生活風格渲染及 360° 旋轉影片 — 過去每件作品需花費數千元的視覺素材。",
        },
        {
          stat: "±0.15mm",
          label: "可製造性檢查",
          body: "Cara 根據真實鑄造公差分析您的設計 — 壁厚、收縮補償、爪鑲強度、邊緣圓角 — 在買家看到之前標記問題。",
        },
        {
          stat: "自動 BOM",
          label: "物料清單",
          body: "即時獲得精確的物料清單 — 金屬重量、寶石數量、合金成本 — 讓您自信定價，買家看到透明的生產估算。",
        },
        {
          stat: "您的故事",
          label: "AI 敘事",
          body: "Cara 為每件作品和系列撰寫編輯級敘事。不是千篇一律的文案 — 而是源自您的設計理念、材料和創作意圖的故事。",
        },
      ],
    },
    capabilities: {
      label: "設計師功能",
      headingLine1: "從檔案到精選",
      headingLine2: "所需一切盡在此處。",
      items: [
        { label: "CAD 驗證", detail: "根據 EPH 製造公差進行結構分析 — 壁厚、倒扣、拔模角度" },
        { label: "產品渲染", detail: "多角度、多金屬、多寶石配置的專業級產品照" },
        { label: "360° 旋轉影片", detail: "動態旋轉預覽，買家可嵌入簡報和提案文件" },
        { label: "造型攝影", detail: "AI 生成的生活風格及模特攝影，匹配您的品牌美學" },
        { label: "系列敘事", detail: "將設計歸入系列，Cara 為所有作品撰寫統一的編輯故事" },
        { label: "材料智能", detail: "針對 10K–24K 金、925 銀、950 鉑金的合金專屬指導 — 收縮率、硬度、表面處理" },
      ],
    },
    workflow: {
      label: "您與 Cara 的工作流程",
      heading: "上傳。驗證。發佈。",
      steps: [
        {
          title: "上傳 CAD",
          body: "將 .3DM、.STL 或 .STEP 檔案拖入 Studio。Cara 讀取幾何結構並立即開始分析。",
        },
        {
          title: "審查與優化",
          body: "Cara 標記可製造性問題、建議修改，並生成完整物料清單。與 Cara 對話迭代 — 詢問合金、公差或替代鑲嵌方案。",
        },
        {
          title: "生成作品集",
          body: "一鍵生成產品渲染、生活風格照、360° 影片和編輯故事。您的設計從原始檔案變為買家就緒的展示。",
        },
        {
          title: "發佈至 Inspire 及目錄",
          body: "一步將驗證完成、渲染就緒的設計推送至您的 Inspire 創作者頁面及貿易目錄。買家發現您的作品時，視覺素材和敘事已就位 — 隨時可授權。",
        },
      ],
    },
    closing: "Cara 不會取代您的創造力 — 它消除設計與需要看到它的買家之間的一切障礙。無需攝影師、無需文案、無需工廠聯絡人。只有您的願景，經過量產驗證，精美呈現。",
    creditsTeaser: {
      label: "Studio 積分",
      heading: "固定成本 AI。無隱藏費用。",
      body: "每項 Cara 操作都有透明的積分成本 — 訊息 5 積分、渲染 15 積分、360° 影片 25 積分。積分包含在您的方案中，隨時可加購。",
      link: "查看積分費用 →",
    },
    finalCta: {
      heading: "您的設計值得被看見。",
      body: "上傳第一個 CAD，讓 Cara 驗證並渲染，發佈至 Inspire — 全程不到五分鐘。",
      ctaDemo: "試用 Cara AI — 即時示範",
      ctaStudio: "開啟 Studio",
    },
  },

  aiCredits: {
    meta: {
      title: "Studio Credits — Caratzon",
      description: "了解 Caratzon 上的 Studio Credits 如何運作——固定成本 AI 操作，用於照片套件、設計諮詢等。無按張收費。",
      socialDescription: "固定成本 AI 操作，用於照片套件、設計諮詢等。",
    },
    hero: {
      label: "Studio Credits",
      heading: "透明、固定成本的 AI。",
      body: "Caratzon 上每項 AI 驅動的操作都消耗固定數量的 Studio Credits。無按張收費、無 token 計量、無意外。",
    },
    howItWorks: {
      label: "運作方式",
      heading: "獲取、使用、充值。",
      steps: [
        { step: "01", title: "獲取積分", body: "付費計劃包含每月積分配額。Explorer 帳戶獲得 200 迎新積分試用。" },
        { step: "02", title: "用於任何操作", body: "照片套件、造型冊、Cara AI 諮詢——每項操作都有預先顯示的固定積分費用。" },
        { step: "03", title: "需要更多時充值", body: "即時購買積分包。充值積分永不過期——訂閱積分每個計費週期重置。" },
      ],
    },
    photoStudio: {
      label: "Photo Studio",
      heading: "生產就緒素材。",
      body: "上傳設計，獲得目錄級攝影、產品線表和行銷文案——全部 AI 生成，數分鐘完成。",
    },
    caraAi: {
      label: "Cara AI",
      heading: "您的設計工程夥伴。",
      body: "Cara 是一位理解冶金學、寶石鑲嵌、結構完整性和製造約束的 AI 珠寶工程師。任何問題都可以問——從材料科學到物料清單估算。",
      note: "發送文字訊息時收取 5 積分。圖像和影片工具在 AI 調用時收費——運行前您會看到費用。典型的 10 條訊息會話加上幾次渲染約使用 100–150 積分。",
    },
    monthlyAllowances: {
      label: "每月配額",
      heading: "您的計劃包含的積分。",
      tableHeaders: ["計劃", "每月積分", "迎新積分", "價格"],
      rows: [
        { plan: "Discover", monthly: "—", welcome: "200", price: "免費" },
        { plan: "Source", monthly: "1,500", welcome: "—", price: "$149/月" },
        { plan: "Atelier", monthly: "5,000", welcome: "—", price: "$399/月" },
        { plan: "Enterprise", monthly: "自訂", welcome: "—", price: "自訂" },
      ],
      note: "訂閱積分每個計費週期重置——未使用的積分不會滾存。充值積分永不過期。",
    },
    topUpPacks: {
      label: "充值包",
      heading: "額外購買，永久保留。",
      body: "推出大型系列？即時購買積分——永不過期。",
      bestValue: "最超值",
      creditsLabel: "積分",
      perCredit: "¢ 每積分",
      buyPrefix: "購買",
      buySuffix: "積分",
      note: "訂閱始終是最划算的——訂閱積分的每積分成本低於充值包。",
      compareLink: "比較計劃",
    },
    faq: {
      label: "常見問題",
      heading: "積分，詳解。",
      items: [
        { q: "與 Cara 對話中途積分用完怎麼辦？", a: "發送前會檢查文字訊息——您會看到充值提示。圖像和影片工具在生成前檢查，AI 會在運行昂貴操作前告知您是否需要更多積分。" },
        { q: "未使用的訂閱積分會滾存嗎？", a: "不會。訂閱積分在每個計費週期開始時重置。如果您經常需要更多，考慮升級計劃或購買充值包——充值積分永不過期。" },
        { q: "單條 Cara 訊息費用會超過 5 積分嗎？", a: "會。基本訊息費用 5 積分，但如果 Cara 在回覆中生成圖像（每張 15 積分）或影片（每段 25 積分），這些會逐步收費。典型的設計諮詢加 2–3 次渲染約 50–70 積分。" },
        { q: "管理員會被收取積分嗎？", a: "不會。管理員帳戶免於積分收費，用於內部測試和運營。" },
        { q: "Essentials Kit 和 Caratzon Kit 有什麼區別？", a: "Essentials Kit（10 積分）生成目錄級照片、規格和 SEO 文案。Caratzon Kit（100 積分）包含 Essentials 的所有內容，加上帶生活攝影、編輯圖像和社交素材的完整造型冊。" },
      ],
    },
    actions: {
      photoStudio: [
        { action: "Essentials Kit", detail: "目錄照片、規格和 SEO 文案" },
        { action: "Caratzon Kit", detail: "完整照片工作室——生活攝影、產品線表和社交素材" },
        { action: "單張照片重新生成", detail: "重新生成一張不滿意的照片" },
        { action: "造型冊 PDF", detail: "品牌批發產品線表" },
      ],
      caraAi: [
        { action: "訊息", detail: "草圖分析、問答、材料科學、結構審查或設計諮詢" },
        { action: "草圖", detail: "根據文字描述快速生成概念草圖" },
        { action: "圖像渲染", detail: "忠實的草圖到概念渲染、情緒板、目錄照片或設計調整" },
        { action: "造型冊圖像", detail: "用於演示文稿的編輯造型冊圖像" },
        { action: "360° 影片", detail: "從每個角度展示您設計的旋轉影片" },
      ],
    },
  },

  academy: {
    meta: {
      title: "學院 — Caratzon",
      description: "為設計師及微品牌提供珠寶製造、品牌建設及行業趨勢的專業指南。",
    },
    hero: {
      title: "Caratzon 學院",
      subtitle: "為珠寶設計師及新興品牌提供專業知識 — 從 CAD 到商業。",
    },
    categories: {
      all: "全部",
      manufacturing: "製造",
      business: "商業",
      trends: "趨勢",
      wholesale: "批發",
      brand: "品牌",
      tech: "科技",
      operations: "運營",
    },
    readingTime: "分鐘閱讀",
    featured: "精選",
    relatedArticles: "相關文章",
    backToAcademy: "← 返回學院",
    cta: {
      title: "準備好將設計變為現實？",
      description: "Caratzon 將獨立設計師與認證製造商連接，實現無縫生產。",
      button: "開始使用 Caratzon →",
    },
    rss: "RSS 訂閱",
    noArticles: "暫無文章，敬請期待！",
  },

  newsroom: {
    meta: {
      title: "Newsroom — Caratzon",
      description:
        "Latest news, press releases, and updates from Caratzon. Company announcements, product launches, partnerships, and industry insights.",
    },
    hero: {
      issueLabel: "Newsroom",
      title: "Latest from Caratzon",
      subtitle: "Company news, press releases, product updates, and industry insights.",
    },
    categories: {
      all: "All",
      company: "Company",
      product: "Product",
      partnership: "Partnership",
      industry: "Industry",
      press_release: "Press Release",
    },
    readingTime: "min read",
    featured: "Featured",
    relatedArticles: "Related Articles",
    backToNewsroom: "← Back to Newsroom",
    cta: {
      title: "Ready to bring your designs to life?",
      description:
        "Caratzon connects independent designers with certified manufacturers for seamless production.",
      button: "Start on Caratzon →",
    },
    noArticles: "No articles yet. Check back soon!",
    pressAssets: {
      meta: {
        title: "Press Assets — Caratzon Newsroom",
        description:
          "Brand logos, guidelines, and media resources for press and partners. Download Caratzon brand assets for editorial use.",
      },
      hero: {
        issueLabel: "Press Assets",
        title: "Press Assets",
        subtitle:
          "Brand resources for press, partners, and editorial use. Download logos and guidelines below.",
      },
      logos: {
        heading: "Brand Logos",
        primary: "Primary logo (light)",
        dark: "Primary logo (dark)",
        icon: "Icon only",
        stacked: "Stacked",
      },
      guidelines: {
        heading: "Brand Guidelines",
        colorPalette: "Color Palette",
        colorNotes:
          "Primary brand accent: Teal Sapphire (#005F6B). Deep India ink for text. Warm cream vellum for backgrounds. Gold leaf reserved for decorative use only.",
        typography: "Typography",
        typographyNotes:
          "Display: Cormorant Garamond, Cormorant SC. Body: Inter. Use display fonts for headlines and editorial accents; Inter for UI and body copy.",
      },
      contact: {
        heading: "Press Contact",
        description: "For media inquiries, press kits, and interview requests:",
        contactLink: "General inquiries →",
      },
    },
  },

  contentKit: {
    nav: "Caratzon Kit",
    meta: {
      title: "Caratzon Kit — Caratzon",
      description: "為您的珠寶設計創建完整的 Caratzon Kit — 專業模特攝影、造型手冊、目錄圖片、社交媒體文案和編輯故事。",
    },
    list: {
      title: "您的 Caratzon Kit",
      subtitle: "從您的設計作品生成行銷就緒的模特攝影、視覺素材、文案和造型手冊。",
      createNew: "新建 Caratzon Kit",
      empty: "尚無 Caratzon Kit。立即創建您的第一個。",
      status: {
        draft: "草稿",
        generating: "生成中",
        ready: "就緒",
        failed: "失敗",
      },
    },
    wizard: {
      step1: "選擇素材來源",
      step2: "生成視覺素材",
      step3: "生成文案",
      step4: "創建造型手冊",
      step5: "發佈與分享",
      source: {
        title: "選擇您的設計來源",
        fromCatalog: "從目錄選取",
        fromCatalogDesc: "選擇您已授權的市場設計作品。",
        uploadNew: "上傳圖片",
        uploadNewDesc: "上傳您自己的珠寶照片。",
        titleLabel: "作品名稱",
        descriptionLabel: "描述",
        next: "生成視覺素材",
      },
      visuals: {
        title: "已生成的視覺素材",
        subtitle: "AI 增強的產品照片和生活風格造型圖。",
        generating: "正在生成您的視覺素材⋯⋯",
        regenerate: "重新生成",
        acceptAll: "接受並繼續",
        catalogShot: "產品照片",
        lookbookShot: "生活風格",
      },
      copy: {
        title: "行銷文案",
        subtitle: "AI 生成的產品描述和社交媒體文案。",
        generating: "正在撰寫您的文案⋯⋯",
        productDescription: "產品描述",
        brandStory: "品牌故事",
        socialCaptions: "社交媒體文案",
        regenerate: "重新生成文案",
        next: "創建造型手冊",
      },
      lookbook: {
        title: "造型手冊預覽",
        subtitle: "您的 AI 組裝造型手冊已準備好下載。",
        generating: "正在組裝您的造型手冊⋯⋯",
        template: "模板風格",
        regenerate: "以不同風格重新生成",
        templates: {
          minimal: "極簡",
          editorial: "編輯風",
          bold: "大膽",
          organic: "自然",
        },
        next: "發佈與分享",
      },
      publish: {
        title: "發佈與分享",
        subtitle: "下載您的 Caratzon Kit 或發佈到探索發現。",
        download: "下載",
        downloadAll: "下載完整 Caratzon Kit",
        downloadImages: "下載圖片 (ZIP)",
        downloadCopy: "下載文案 (TXT)",
        downloadPdf: "下載造型手冊 (PDF)",
        publish: "發佈到探索發現",
        publishDesc: "在 Caratzon 上公開您的探索發現頁面。",
        slugLabel: "探索發現網址",
        categoryLabel: "類別",
        materialsLabel: "材質",
        keywordsLabel: "風格關鍵詞",
        heroLabel: "主圖",
        published: "已發佈！",
        viewDiscovery: "查看探索發現頁面",
      },
    },
    upsell: {
      title: "Studio Credits 已用完",
      body: "升級至 Source 每月獲得 1,500 Studio Credits——足夠生成 15 個 Caratzon Kit。或即時購買充值包。",
      cta: "查看方案",
    },
  },

  concierge: {
    title: "Caratzon 禮賓服務",
    subtitle: "您的 B2B 珠寶採購助手",
    welcome: "歡迎！您可以詢問我們的設計、授權方案、製造流程，或任何關於 Caratzon 的問題。",
    inputPlaceholder: "詢問設計、授權、定價...",
    disclaimer: "AI 驅動，回覆可能不完全準確。",
    quickActions: [
      "授權如何運作？",
      "我是設計師——如何加入？",
      "有哪些定價方案？",
      "我想聯繫真人客服",
    ],
    leadForm: {
      title: "與我們的團隊聯繫",
      description: "請分享您的資訊，Caratzon 團隊將在 24 小時內跟進。",
      name: "您的姓名",
      email: "商務電郵",
      company: "公司名稱（選填）",
      message: "請簡要描述您需要的幫助...",
      submit: "聯繫團隊",
      successTitle: "已連接",
      success: "Caratzon 團隊將在 24 小時內與您聯繫。您可以繼續在此聊天。",
    },
    handoffConfirm: {
      title: "已請求轉接",
      description: "我們已將您的請求詳情通知 Caratzon 團隊。團隊成員將盡快與您聯繫。您可以繼續在此聊天，或直接發送郵件至 trade@caratzon.com。",
    },
  },

  inspire: {
    meta: {
      title: "靈感 — Caratzon",
      description: "探索來自世界各地設計師和品牌的精美珠寶設計。",
    },
    explore: {
      title: "靈感",
      subtitle: "探索來自世界各地創作者的珠寶設計。",
      allCategories: "全部",
      noResults: "此類別中未找到相關作品。",
    },
    profile: {
      pieces: "作品",
      totalViews: "總瀏覽量",
    },
    detail: {
      by: "作者",
      views: "瀏覽",
      materials: "材質",
      share: "分享",
      viewProfile: "查看個人檔案",
      cta: {
        title: "創建您自己的靈感頁面",
        body: "加入設計師行列，透過 AI 生成的造型手冊、編輯故事和專屬個人檔案，在您自己的 Studio 中展示您的作品。",
        button: "開始創作",
      },
    },
    saved: {
      title: "已收藏作品",
      empty: "暫無收藏作品。",
      emptySubtitle: "瀏覽靈感頁面，發現您的心儀之作。",
      backToInspire: "返回靈感",
    },
    creatorOnboarding: {
      meta: {
        title: "加入 Inspire 成為創作者 · Caratzon",
        description: "告訴我們您的工藝、故事與願景——讓我們向世界展示您。",
      },
      header: "Inspire · 創作者入駐",
      stepOf: "第 {step} 步，共 {total} 步",
      back: "← 返回",
      continue: "繼續 →",
      steps: {
        profile: "個人檔案",
        styleTools: "風格與工具",
        creativeDna: "創意 DNA",
        philosophy: "設計理念",
        innerWorld: "內心世界",
        production: "生產準備",
        review: "審閱",
      },
      profile: {
        title: "您的設計師檔案",
        subtitle: "買家及採購經理將在平台上看到這些資訊。",
        firstName: "名 *",
        lastName: "姓 *",
        studioName: "工作室 / 品牌名稱",
        studioPlaceholder: "例如 Studio Noir、Aria Atelier",
        location: "所在地 / 城市",
        locationPlaceholder: "例如 巴黎、法國",
        photo: "個人照片",
        photoOptional: "（選填 — AI 增強）",
        photoHint: "上傳頭像或生活照。我們的 AI 將以溫暖影樓燈光及簡潔背景增強效果。",
        photoEnhancing: "正在以 AI 增強您的照片…",
        photoSaved: "照片已增強並儲存 ✓",
        photoChange: "更換",
        photoUpload: "上傳",
      },
      style: {
        title: "您的美學與工具",
        subtitle: "我們以此將您的作品與合適的買家受眾配對。",
        aestheticLabel: "您如何形容您的設計美學？ *",
        aestheticHint: "可多選",
        designStyleLabels: {
          "Minimalist": "極簡",
          "Vintage-inspired": "復古風格",
          "Bridal/Engagement": "婚嫁 / 訂婚",
          "Men's": "男士",
          "Bold/Chunky": "大膽 / 厚實",
          "Trend-driven Fashion": "潮流時尚",
          "Everyday Fine": "日常精品",
          "Maximalist": "極繁",
        } as Record<string, string>,
        cadLabel: "您主要使用哪種 CAD 軟件？ *",
        cadHint: "可多選",
        formatsLabel: "您可提供哪些檔案格式？",
        formatsHint: "可多選",
      },
      creative: {
        title: "您的創意 DNA",
        subtitle: "幫助我們了解您工藝的根源。這些回答將塑造 AI 如何向買家講述您的故事。",
        originLabel: "您如何成為珠寶設計師？ *",
        originHint: "您的起源故事——甚麼吸引您投身這門工藝？童年回憶、導師、轉捩點？",
        originPlaceholder: "我從小看著祖母在齋浦爾的工作坊裡將米珠串上絲線。光線落在每顆珠子上時，對我而言就像魔法…",
        materialsLabel: "您對哪些材質最有共鳴？",
        materialsHint: "選出所有有共鳴的選項",
        signatureMaterialLabels: {
          "Gold (yellow, rose, white)": "黃金（黃金、玫瑰金、白金）",
          "Platinum": "鉑金",
          "Silver / Sterling": "銀 / 純銀",
          "Titanium": "鈦",
          "Enamel": "琺瑯",
          "Precious gemstones": "珍貴寶石",
          "Semi-precious stones": "半寶石",
          "Pearls": "珍珠",
          "Diamonds": "鑽石",
          "Wood / organic materials": "木材 / 天然材料",
          "Recycled metals": "再生金屬",
          "Lab-grown stones": "實驗室培育寶石",
          "Mixed media": "混合媒材",
        } as Record<string, string>,
        ritualsLabel: "描述您的創作儀式",
        ritualsHint: "您的流程是怎樣的？先畫草圖、直接做 CAD、還是用蠟模？您在哪裡工作？",
        ritualsPlaceholder: "我總是用紋理紙上的水彩草圖開始。我需要先感受重量與流動，才會觸碰螢幕…",
        culturalLabel: "哪些文化或歷史影響塑造了您的作品？",
        culturalHint: "傳統、地區、時代、藝術運動——任何滋養您視覺語言的元素。",
        culturalPlaceholder: "裝飾藝術幾何、莫臥兒花卉紋樣、日式侘寂的不完美…",
      },
      philosophy: {
        title: "您的設計理念",
        subtitle: "這些問題揭示您如何思考工藝——張力、信念與選擇，令您的作品獨一無二。",
        oneWordLabel: "若要用一個詞形容您的設計理念，會是甚麼？ *",
        oneWordPlaceholder: "例如 張力、崇敬、叛逆、流動",
        feelingLabel: "當有人佩戴您的珠寶時，他們應該感受到甚麼？ *",
        feelingHint: "不是他們應該看起來怎樣——而是作品應喚起甚麼情感狀態？",
        feelingPlaceholder: "像懷揣一個秘密——珍貴且完全屬於自己。一種無需張揚的靜默自信。",
        tensionLabel: "甚麼創作張力定義了您的作品？",
        tensionHint: "每位設計師都在權衡取捨。裝飾 vs. 克制？傳統 vs. 顛覆？完美 vs. 粗獷？",
        tensionPlaceholder: "我總在精準與不完美之間拉扯。我希望每條線都經過深思，但會留空間讓金屬給我驚喜…",
        opinionLabel: "您對珠寶設計有甚麼非主流看法？",
        opinionHint: "您相信但業內大多數人會反對的觀點。",
        opinionPlaceholder: "我認為對稱被高估了。最美的作品有一種刻意的非對稱，令它們充滿生命力…",
      },
      innerWorld: {
        title: "您的內心世界",
        subtitle: "這些更深層的問題幫助我們的 AI 了解工作台以外的您——令您的設計師故事真實呈現。",
        inspirationLabel: "珠寶以外，甚麼啟發您的設計？ *",
        inspirationHint: "建築、音樂、自然、美食、電影、數學、一座城市、一種感覺——任何事物。",
        inspirationPlaceholder: "粗獷主義混凝土捕捉午後光線的方式。鸚鵡螺殼中的數學螺旋。日式枯山水庭園的靜謐…",
        emotionalLabel: "設計為您滿足了甚麼情感需求？",
        emotionalHint: "您為何持續創作？沒有它，您的生活會缺少甚麼？",
        emotionalPlaceholder: "那是我思緒唯一安靜下來的時刻。當我沉浸於設計時，喧囂停止，只剩下作品與我…",
        legacyLabel: "二十年後，您希望您的作品體系訴說甚麼？",
        legacyHint: "您的傳承願景——甚麼主線應貫穿您創造的一切？",
        legacyPlaceholder: "我製作了人們不只佩戴而是珍藏的作品。成為某人故事一部分的物件，傳承下去不是因為價值，而是因為意義…",
        metaphorLabel: "若您的設計實踐是一個地方，它會是甚麼模樣？",
        metaphorHint: "您創意空間的比喻——真實或想像。",
        metaphorPlaceholder: "懸崖邊的玻璃工作室。精密的工具，狂野的景觀。室內一切井然有序；室外一切未經馴服…",
      },
      production: {
        title: "生產準備",
        subtitle: "製造商需要針對實體生產優化的 CAD 檔案——鑲爪厚度、金屬重量、收縮公差。",
        cadOptimisedLabel: "您的 CAD 檔案是否已完全針對實體製造優化？",
        optionYes: "是 — 我的檔案已完全針對製造優化",
        optionMostly: "大致是 — 可能需要微調",
        optionNo: "否 — 我僅為渲染/視覺化而設計",
        optionHelp: "我需要協助優化檔案",
        portfolioLabel: "作品集 / 網站",
        portfolioPlaceholder: "https://yourportfolio.com",
        socialLabel: "社交帳戶",
        socialOptional: "（選填 — 顯示於您的 Inspire 頁面）",
        instagramPlaceholder: "@yourstudio 或 https://instagram.com/yourstudio",
        pinterestPlaceholder: "@yourstudio 或 https://pinterest.com/yourstudio",
        tiktokPlaceholder: "@yourstudio 或 https://tiktok.com/@yourstudio",
        sampleUploadsTitle: "樣本上傳",
        sampleUploadsBody: "完成檔案後，您可從 Studio 控制台直接上傳 CAD 渲染圖及成品照片。",
      },
      review: {
        title: "審閱您的檔案",
        subtitle: "一切正確嗎？審批通過後，您可從 Studio 設定中更新這些資訊。",
        aiEnhanced: "AI 增強肖像",
        labels: {
          name: "姓名",
          studio: "工作室",
          location: "所在地",
          aesthetic: "美學",
          cadSoftware: "CAD 軟件",
          fileFormats: "檔案格式",
          productionReady: "生產就緒",
          portfolio: "作品集",
          instagram: "Instagram",
          pinterest: "Pinterest",
          tiktok: "TikTok",
        },
        creativeProfile: "您的創意檔案",
        creativeLabels: {
          originStory: "起源故事",
          materials: "材質",
          philosophy: "理念",
          wearerFeels: "佩戴者感受",
          inspiration: "靈感",
          legacy: "傳承",
        },
        storyGeneration: {
          title: "生成您的設計師故事",
          subtitle: "我們的 AI 將根據問卷回答撰寫引人入勝的敘事——準備發佈於您的 Inspire 頁面。",
          button: "生成我的故事",
          loading: "正在撰寫您的敘事…",
          error: "故事生成失敗",
          tryAgain: "重試",
          refineLater: "審批通過後，您可從 Studio 控制台精煉此故事。",
        },
        terms: {
          prefix: "我確認以上資訊準確，並同意 Caratzon 的",
          termsLink: "設計師服務條款",
          and: "及",
          privacyLink: "私隱政策",
        },
        submit: "提交申請 →",
        submitting: "提交中…",
      },
      errors: {
        nameRequired: "請輸入您的姓名。",
        agreeRequired: "您必須同意設計師條款才能繼續。",
        submissionFailed: "提交申請時出錯，請重試。",
      },
      pending: {
        meta: {
          title: "申請審核中 · Caratzon",
          description: "您的設計師申請正在由我們的團隊審核。",
        },
        overline: "私人測試版",
        thankYou: "感謝您，{name}",
        thankYouGeneric: "感謝您",
        submitted: "您的申請已成功提交。Caratzon 目前處於",
        privateBeta: "私人測試版",
        submittedSuffix: "，我們的團隊會親自審核每份設計師申請。",
        emailNotice: "帳戶獲批後我們將以電郵通知您。通常需 1–2 個工作天。",
        backToHome: "返回首頁",
        exploreAcademy: "探索學院 →",
        rejected: {
          overline: "申請更新",
          heading: "我們目前無法批准您的申請",
          body: "感謝您對 Caratzon 的興趣。很遺憾，我們目前無法批准您的申請。若您認為有誤或希望提供補充資料，請聯絡我們的團隊。",
          feedbackLabel: "團隊反饋",
          contactUs: "聯絡我們",
          reapply: "重新申請",
          reapplying: "提交中…",
        },
      },
    },
  },

  caraChat: {
    empty: {
      title: "認識 Cara",
      description: "您的 AI 設計助手 — 集珠寶大師與機械工程師於一身。描述您的構想、上傳草圖，或拖放靈感圖片開始創作。",
      sketchToConcept: "草圖 → 概念",
      photoToDesign: "照片 → 設計",
      ideaToRender: "構想 → 渲染",
    },
    placeholder: {
      initial: "描述您的構想、上傳草圖，或拖放靈感圖片…",
      direction: "告訴 Cara 設計方向 — 材質、風格、年代…",
      adjust: "要求 Cara 調整設計，或進行結構分析…",
      finalize: "準備定稿？讓 Cara 生成目錄渲染圖和材料清單。",
    },
    dragOverlay: "拖放草圖或參考圖片",
    scrollPill: "↓ 新訊息",
    sketchFallback: "這是我的草圖/參考圖片，請分析。",
    error: "出了點問題，請重試。",
    regenerate: "重新生成",
    suggestions: {
      custom: "其他…",
      customPlaceholder: "輸入您的答案…",
      send: "發送",
    },
    editMessage: "編輯訊息",
    editSend: "發送",
    editCancel: "取消",
    download: "下載",
    copyMessage: "複製訊息",
    justNow: "剛剛",
    minutesAgo: "{n}分鐘前",
    toolLabels: {
      generate_mood_board: "情緒板",
      generate_concept_sketch: "概念草圖",
      generate_design_render: "設計渲染",
      adjust_design: "調整渲染",
      generate_catalog_render: "目錄渲染",
      render_from_sketch: "草圖概念",
      analyze_sketch: "草圖分析",
      analyze_inspiration: "靈感分析",
      reference_lookup: "參考查詢",
      structural_analysis: "結構分析",
      calculate_bom: "材料清單",
      material_recommendation: "材質分析",
      generate_turntable_video: "旋轉影片",
      generate_lookbook_image: "造型照",
      present_design_specs: "設計規格",
      finalize_design: "設計定稿",
      share_to_gallery: "分享至畫廊",
    },
    toolLoading: {
      rendering: "渲染中…",
      sketchingConcept: "繪製概念中…",
      analyzingSketch: "分析草圖中…",
      analyzingInspiration: "分析靈感中…",
      lookingUpReference: "查詢參考資料中…",
      generatingVideo: "生成影片中 — 最多需要一分鐘…",
      stylingLookbook: "製作造型照中…",
      structuralAnalysis: "執行可製造性模擬中…",
      materialCheck: "檢查材質相容性中…",
      calculatingBom: "計算材料清單中…",
      sharingToGallery: "分享至畫廊中…",
      preparingSpecs: "準備規格中…",
      finalizingDesign: "定稿設計中…",
      processing: "處理中…",
    },
    toolCards: {
      moodBoard: "情緒板",
      conceptSketch: "概念草圖",
      refinedSketch: "精修草圖",
      turntablePreview: "旋轉預覽",
      sketchAnalysis: "草圖分析",
      inspirationAnalysis: "靈感分析",
      structuralAnalysis: "結構分析",
      billOfMaterials: "材料清單",
      materialAnalysis: "材質分析",
      designFinalized: "設計已定稿",
      sharedToGallery: "已分享至畫廊",
      errorFallback: "出了點問題。",
      designRender: "設計渲染",
      updatedRender: "更新渲染",
      catalogRender: "目錄渲染",
      conceptFromSketch: "草圖概念",
      lookbookLabel: "造型照",
      saveToStudio: "儲存至 Studio",
      savedOpenStudio: "已儲存 — 開啟 Studio",
      submitBrief: "提交設計簡報",
      briefSubmitted: "簡報已提交 — 查看訂單",
      designTitlePlaceholder: "設計標題…",
      saving: "儲存中…",
      save: "儲存",
      cancel: "取消",
      structural: {
        manufacturabilityReport: "可製造性報告",
        areasAnalyzed: "已分析 {n} 個區域",
        areasAnalyzedPlural: "已分析 {n} 個區域",
        issuesFound: "發現問題",
        warnings: "警告",
        allClear: "全部通過",
        pass: "通過",
        warning: "警告",
        warningPlural: "警告",
        critical: "嚴重",
        recommendationLabel: "建議：",
        addressCritical: "請在鑄造前解決嚴重問題。",
        reviewWarnings: "請檢查上述警告 — 設計可製造，但需作調整。",
      },
      bom: {
        costBreakdown: "成本明細",
        lineItems: "{n} 個項目",
        lineItemsPlural: "{n} 個項目",
        item: "項目",
        qty: "數量",
        estCost: "預估成本",
        laborSetting: "工費及鑲嵌",
        totalEstimate: "預估總計",
      },
      material: {
        compatibilityCheck: "相容性檢查",
        compatible: "相容",
        concerns: "需注意",
        concernsLabel: "需注意事項",
        recommendationsLabel: "建議",
        allCompatible: "所有材質均可用於生產。",
      },
      sketch: {
        sketchInterpretation: "草圖解讀",
        needsInput: "需要確認",
        stones: "寶石",
        filigree: "花絲工藝",
        engraving: "雕刻",
        designNotes: "設計備註",
        needsClarification: "待確認事項",
      },
      inspiration: {
        inspirationBreakdown: "靈感解析",
        colorPalette: "色彩",
        motifs: "圖案元素",
        textures: "質感",
        suggestedFinishes: "建議表面處理",
        suggestedForms: "建議珠寶形態",
      },
      designSpecs: {
        weight: "重量",
        stones: "寶石",
        stoneSingular: "寶石",
        finish: "表面處理",
        stonesLabel: "寶石",
        dimensions: "尺寸",
        manufacturable: "可製造",
        manufacturabilityConcerns: "製造性疑慮",
      },
      finalize: {
        productionReady: "可量產",
        metal: "金屬",
        stones: "寶石",
        stoneSingular: "寶石",
        estRetail: "預估零售價",
        cadHandoff: "可進行 CAD 交付、鑄造或報價",
      },
      gallery: {
        nowInGallery: "已加入畫廊",
        storyGenerating: "設計故事生成中 — 請至畫廊頁面查看。",
      },
      reference: {
        title: "參考數據",
        casting: "鑄造規格",
        metals: "金屬特性",
        stones: "寶石耐久性",
        settings: "鑲嵌相容性",
      },
    },
    starters: [
      { label: "單鑽戒指", text: "設計一枚18K玫瑰金鑲嵌1克拉橢圓形鑽石的現代單鑽訂婚戒指" },
      { label: "裝飾藝術雞尾酒戒指", text: "創作一枚以裝飾藝術幾何風格為靈感、鑲嵌祖母綠和縞瑪瑙的大膽雞尾酒戒指" },
      { label: "網球手鏈", text: "設計一條鉑金鑲嵌藍寶石和鑽石交替排列的極簡網球手鏈" },
    ],
    buyerOverrides: {
      empty: {
        title: "用Cara設計",
        description: "描述您想要的珠寶——風格、材質、場合——Cara將以可量產的渲染圖呈現您的願景。",
      },
      placeholder: {
        initial: "描述您想要的珠寶——風格、材質、場合……",
        direction: "告訴Cara更多偏好——材質、預算、數量……",
        adjust: "讓Cara調整設計，或探索不同的變化……",
        finalize: "對設計滿意？提交為量產設計簡報。",
      },
      starters: [
        { label: "訂製訂婚戒指", text: "我想要一枚訂製訂婚戒指——橢圓形鑽石、現代鑲嵌、玫瑰金" },
        { label: "品牌系列單品", text: "我需要一款品牌標誌性吊墜——極簡風格、14K金、零售價200美元以下" },
        { label: "婚戒套裝", text: "設計一對鉑金情侶婚戒，帶有精緻細節" },
      ],
    },
  },

  demo: {
    meta: {
      title: "Cara AI 示範 · Caratzon",
      description: "試用 Cara——為工廠和人手而設計的珠寶工程 AI。收縮率、公差、鑄造力學、佩戴舒適度——每個概念均可直接投產。即場示範。",
    },
    splash: {
      eyebrow: "珠寶工程 AI",
      heading: "認識 Cara",
      subheading: "工程 AI，設計工廠能製造、顧客想佩戴的珠寶——從第一張草圖開始。",
      cta: "開始設計",
      enterHint: "按 {key} 開始",
      promptDivider: "或試試以下提示",
      signIn: "登入",
      noAccount: "無需帳戶",
      notSaved: "對話不會被儲存",
      fullAccess: "完整平台存取 →",
      namePlaceholder: "您的名字（顯示在展覽中）",
      nameHint: "用於標註您的創作",
    },
    prompts: [
      { label: "訂婚戒指", text: "設計一枚現代單石訂婚戒指，配 1 克拉橢圓形鑽石，18K 玫瑰金" },
      { label: "Art Deco 雞尾酒戒指", text: "創作一枚大膽的 Art Deco 風格雞尾酒戒指，配祖母綠和瑪瑙" },
      { label: "網球手鏈", text: "設計一條極簡網球手鏈，藍寶石與鑽石交替排列，鉑金材質" },
      { label: "雕塑手鐲", text: "我想要一個有機流線造型的雕塑金手鐲，不鑲嵌寶石，純粹金屬工藝" },
    ],
    capabilities: [
      { label: "草圖到生產藍圖", desc: "上傳手繪草圖——Cara 為工廠進行工程設計" },
      { label: "鑄造與收縮分析", desc: "金屬特定收縮補償、壁厚驗證" },
      { label: "寶石公差映射", desc: "爪位、座深和腰圍間隙——工程設計" },
      { label: "佩戴感與舒適度", desc: "戒圈輪廓、重量平衡、邊緣半徑——為全天佩戴而設計" },
    ],
    header: {
      brandLabel: "Cara AI",
      tabs: { create: "創作", gallery: "展覽" },
      back: "← 返回",
      fullAccess: "獲取完整存取",
    },
    gallery: {
      loading: "載入展覽中…",
      emptyTitle: "尚無設計",
      emptyBody: "成為第一個創作者。示範期間生成的設計將在此展示給所有人。",
      emptyAction: "創作第一個設計",
      sectionTitle: "在此活動中創作",
      countSingular: "{count} 個設計由示範訪客生成",
      countPlural: "{count} 個設計由示範訪客生成",
      createYours: "創作您的",
      createdWith: "由 Cara AI 創作",
      generating: "正在生成故事…",
      viewStory: "查看故事",
      toolLabels: {
        render: "AI 渲染",
        mood_board: "靈感板",
        catalog_render: "目錄渲染",
      },
      fallbackTool: "渲染",
      timeAgo: {
        justNow: "剛剛",
        minutes: "{n}分鐘前",
        hours: "{n}小時前",
        days: "{n}天前",
      },
    },
    story: {
      backToGallery: "← 返回展覽",
      fromSketch: "手繪稿",
      theSketch: "草圖",
      sketchCaption: "{name} 的原創草圖",
      theDetails: "設計細節",
      detailType: "類型",
      detailMetal: "金屬",
      detailStyle: "風格",
      detailStones: "寶石",
      detailFinish: "表面處理",
      detailWeight: "重量",
      detailPrice: "預估價格",
      structuralAnalysis: "結構分析",
      theStory: "故事",
      showcase: "展示",
      designJourney: "設計歷程",
      theConversation: "設計對話",
      moreMessages: "對話中還有 {n} 條更多訊息",
      createdBy: "創作者",
      createdWith: "在{event}使用 Cara AI 創作",
      eventName: "即場示範",
      tryIt: "親自試用 Cara →",
      artifactLabels: {
        render: "設計渲染",
        catalog_render: "目錄渲染",
        concept_sketch: "概念草圖",
        mood_board: "情緒板",
        structural_analysis: "結構分析",
        material_recommendation: "材料分析",
        bom: "物料清單",
        sketch_analysis: "草圖分析",
        lookbook: "造型照",
        video: "旋轉影片",
        inspiration: "靈感分析",
        design_specs: "設計規格",
        finalize_design: "最終設計",
        gallery_share: "畫廊分享",
      },
    },
    brands: {
      splash: {
        eyebrow: "品牌設計智能",
        subheading: "描述您的願景。即時渲染。直接投產。",
        cta: "開始探索",
        namePlaceholder: "您的姓名或品牌",
        nameHint: "為您提供個性化體驗",
        fullAccess: "開設貿易賬戶 →",
      },
      prompts: [
        { label: "訂製網球手鏈", text: "為我的春季系列將這條網球手鏈訂製為玫瑰金" },
        { label: "品牌標誌吊墜", text: "用我們品牌的幾何圖案創作一款標誌性吊墜 — 極簡風格、14K金" },
        { label: "婚戒選擇", text: "展示三個價位的極簡婚戒套裝選項" },
        { label: "草圖到量產", text: "我有一個手鐲草圖 — 能做成可量產的設計嗎？" },
      ],
      capabilities: [
        { label: "訂製預覽", desc: "以照片級渲染查看您的設計願景" },
        { label: "材質與表面處理", desc: "並排比較金屬、寶石和表面處理" },
        { label: "樣品報價", desc: "原型製作和小批量生產的即時估價" },
        { label: "生產時間線", desc: "從概念到交付 — 查看完整製造路徑" },
      ],
      header: {
        fullAccess: "開設貿易賬戶",
      },
    },
  },

  // ─── Production Dashboard ─────────────────────────────────────────────────
  productionDashboard: {
    title: "生產儀表板",
    tabs: {
      portfolio: "總覽",
      board: "看板",
    },
    zoom: "縮放",
    day: "日",
    week: "週",
    month: "月",
    search: "搜尋...",
    searchPlaceholder: "搜尋計劃、設計、製造商...",
    keyboardShortcuts: "鍵盤快捷鍵",
    groupBy: "分組方式",
    status: "狀態",
    phase: "階段",
    dragDisabledInPhase: "階段檢視中無法拖曳",
    backToPortfolio: "總覽",
    subOrders: "子訂單",
    unassigned: "未分配",
    noItems: "暫無項目",
    densityCompact: "緊湊檢視",
    densityComfortable: "舒適檢視",

    empty: {
      portfolioTitle: "暫無進行中的生產",
      portfolioDescription: "在生產規劃器中創建的計劃獲批後將顯示於此。",
      portfolioLink: "開啟生產規劃器",
      boardTitle: "暫無子訂單",
      boardDescription: "沒有符合篩選條件的子訂單。",
      boardLink: "查看總覽",
      planTitle: "暫無子訂單",
      planDescription: "此計劃尚未建立子訂單。",
      planLink: "開啟生產規劃器",
    },

    filters: {
      allStatuses: "所有狀態",
      allDesigners: "所有設計師",
      allBuyers: "所有買家",
    },

    command: {
      noResults: "未找到結果。",
      productionPlans: "生產計劃",
      quickActions: "快速操作",
      goToPortfolio: "前往總覽",
      goToBoard: "前往看板",
      navigate: "導航",
      open: "開啟",
      close: "關閉",
    },

    kanban: {
      blockedAndCancelled: "受阻及已取消",
      columns: {
        PENDING: "待處理",
        QUOTED: "已報價",
        ASSIGNED: "已分配",
        IN_PROGRESS: "進行中",
        QA: "品質管控",
        COMPLETED: "已完成",
        BLOCKED: "受阻",
        CANCELLED: "已取消",
      },
    },

    gantt: {
      today: "今天",
      criticalPath: "關鍵路徑",
      duration: "工期",
      days: "天",
      manufacturer: "製造商",
      dependsOn: "依賴於",
    },

    detail: {
      status: "狀態",
      manufacturer: "製造商",
      duration: "工期",
      dependencies: "依賴項目",
    },

    hover: {
      duration: "工期",
      manufacturer: "製造商",
    },
  },

  // ─── Buyer Milestone Tracker ──────────────────────────────────────────────
  buyerTracker: {
    title: "生產進度",
    inProgress: "進行中",
    pending: "待處理",
    milestones: {
      orderConfirmed: "訂單確認",
      designEngineering: "設計工程",
      sampleProduction: "樣品生產",
      approval: "審批確認",
      manufacturing: "量產製造",
      finalFinishing: "最終修飾",
      qualityCheckDelivery: "品質檢測及交付",
    },
  },
};
